(() => {
  const onReady = (fn) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  };

  onReady(() => {
    const splash = document.querySelector('[data-jol-splash]');
    if (splash) {
      document.documentElement.classList.add('jol-has-splash');
      document.body.classList.add('jol-has-splash');
      let entered = false;
      try {
        if (window.sessionStorage.getItem('jolSplashEntered') === '1') {
          entered = true;
          document.documentElement.classList.add('jol-entered-session');
          document.body.classList.add('jol-entered');
          splash.setAttribute('aria-hidden', 'true');
        }
      } catch (e) {}
      const splashChrome = splash.dataset.jolBrowserChromeColor || '#214b32';
      const storeChrome = document.querySelector('[data-jol-homepage]')?.dataset.jolBrowserChromeColor || '#1f120a';
      window.jolSetBrowserChrome?.(entered ? 'store' : 'splash', entered ? storeChrome : splashChrome);
    }

    document.querySelectorAll('[data-jol-splash-logo]').forEach((link) => {
      link.addEventListener('click', (event) => {
        try { window.sessionStorage.removeItem('jolSplashEntered'); } catch (e) {}

        const currentSplash = document.querySelector('[data-jol-splash]');
        if (!currentSplash) return;

        event.preventDefault();
        currentSplash.classList.remove('is-entering');
        currentSplash.removeAttribute('aria-hidden');
        document.documentElement.classList.remove('jol-entered-session');
        document.body.classList.remove('jol-entered');
        window.jolSetBrowserChrome?.('splash', currentSplash.dataset.jolBrowserChromeColor || '#214b32');
      });
    });

    document.querySelectorAll('[data-jol-shuffle]').forEach((button) => {
      button.addEventListener('click', () => {
        const section = button.closest('[data-jol-collection]');
        const grid = section?.querySelector('[data-jol-product-grid]');
        if (!grid) return;

        // V222: only shuffle actual product cards; deck end-caps / controls stay fixed.
        const cards = Array.from(grid.children).filter((child) => child.matches?.('.jol-card'));
        for (let i = cards.length - 1; i > 0; i -= 1) {
          const j = Math.floor(Math.random() * (i + 1));
          [cards[i], cards[j]] = [cards[j], cards[i]];
        }
        cards.forEach((card) => grid.appendChild(card));

        // V195: keep the physical button press independent from the shuffle flourish.
        // Animating the button transform here used to cancel/reset the depressed state early.
        const shuffleIcon = button.querySelector('svg');
        shuffleIcon?.animate(
          [
            { transform: 'rotate(0deg)' },
            { transform: 'rotate(-16deg)' },
            { transform: 'rotate(13deg)' },
            { transform: 'rotate(0deg)' }
          ],
          { duration: 360, easing: 'ease-out' }
        );
      });
    });

    // V224: the homepage nav always remains visually fused to the leather hero/banner.
    // Shop Shirts no longer changes its color/depth state.
    const homepage = document.querySelector('[data-jol-homepage]');
    const homepageHeader = homepage?.querySelector('.jol-header') || document.querySelector('.jol-header');
    if (homepage && homepageHeader) homepageHeader.classList.add('jol-header--hero-seamless');

    // V229: Opera/iPhone hard-locks the mobile deck geometry to a pixel height.
    // iOS browser chrome can fire repeated viewport-resize events while the bottom
    // navigation bar opens/closes. CSS viewport units are not reliable enough in
    // every iOS browser for this specific boundary, so derive the height from the
    // physical CSS screen size and DO NOT update it on ordinary resize events.
    // Recalculate only when the device orientation actually changes.
    const lockMobileDeckViewport = () => {
      if (!homepage || !window.matchMedia?.('(max-width: 760px)')?.matches) return;

      const screenW = Number(window.screen?.width) || window.innerWidth || 390;
      const screenH = Number(window.screen?.height) || window.innerHeight || 844;
      const portrait = window.matchMedia?.('(orientation: portrait)')?.matches !== false;
      const stableScreenHeight = portrait
        ? Math.max(screenW, screenH)
        : Math.min(screenW, screenH);
      const deckHeight = Math.min(590, Math.round(stableScreenHeight * 0.64));

      document.documentElement.style.setProperty('--jol-mobile-deck-static-height', `${deckHeight}px`);
      document.documentElement.style.setProperty('--jol-mobile-layout-static-height', `${Math.round(stableScreenHeight)}px`);
    };

    lockMobileDeckViewport();
    window.addEventListener('orientationchange', () => {
      window.setTimeout(lockMobileDeckViewport, 320);
    }, { passive: true });

    document.querySelectorAll('[data-jol-scroll]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const href = link.getAttribute('href');
        if (!href || !href.startsWith('#')) return;
        const target = document.querySelector(href);
        if (!target) return;
        event.preventDefault();

        // V227: Horizon uses .page-wrapper as the actual desktop scroll container.
        // Scroll that element on desktop instead of window, while keeping native
        // document scrolling on tablet/mobile. In both cases the note's top rule
        // lands immediately below the homepage nav.
        const stickyHeader = homepageHeader || document.querySelector('.jol-header');
        const headerHeight = stickyHeader?.getBoundingClientRect?.().height || 0;
        const desktopWrapper = window.matchMedia?.('(min-width: 990px)')?.matches
          ? document.querySelector('.page-wrapper')
          : null;

        if (desktopWrapper) {
          const wrapperRect = desktopWrapper.getBoundingClientRect();
          const targetRect = target.getBoundingClientRect();
          const targetTop = desktopWrapper.scrollTop + targetRect.top - wrapperRect.top - headerHeight;
          desktopWrapper.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
        } else {
          const root = document.scrollingElement || document.documentElement;
          const targetTop = root.scrollTop + target.getBoundingClientRect().top - headerHeight;
          root.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
        }
      });
    });

    // V227: on mobile, a vertical drag that starts inside a product stack belongs
    // entirely to that stack. Never leak the tail of that gesture to page scrolling;
    // the blank gutter beside the cards remains the deliberate way to move the page
    // down to More from luck jar / Contact. Horizontal gestures are left untouched so
    // users can still swipe between Featured, Graphics, LJ Supply, etc.
    document.querySelectorAll('[data-jol-deck-browser] .jol-deck__scroller.jol-merch__scroll-mobile').forEach((scroller) => {
      let startX = 0;
      let startY = 0;
      let lastY = 0;
      let verticalGesture = false;

      scroller.addEventListener('touchstart', (event) => {
        if (!window.matchMedia?.('(max-width: 760px)')?.matches || event.touches.length !== 1) return;
        const touch = event.touches[0];
        startX = touch.clientX;
        startY = touch.clientY;
        lastY = touch.clientY;
        verticalGesture = false;
      }, { passive: true });

      scroller.addEventListener('touchmove', (event) => {
        if (!window.matchMedia?.('(max-width: 760px)')?.matches || event.touches.length !== 1) return;
        const touch = event.touches[0];
        const totalX = touch.clientX - startX;
        const totalY = touch.clientY - startY;
        const stepY = touch.clientY - lastY;
        lastY = touch.clientY;

        if (!verticalGesture && Math.abs(totalY) > Math.abs(totalX) + 4) verticalGesture = true;
        if (!verticalGesture) return;

        // Keep native momentum while there is stack content left to scroll. Only
        // cancel the gesture at the actual top/bottom boundary so iOS/Android cannot
        // hand the remainder to the page and tug More from luck jar into view.
        const maxScroll = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
        const atTop = scroller.scrollTop <= 1;
        const atBottom = scroller.scrollTop >= maxScroll - 1;
        if ((atTop && stepY > 0) || (atBottom && stepY < 0)) event.preventDefault();
      }, { passive: false });

      scroller.addEventListener('touchend', () => { verticalGesture = false; }, { passive: true });
      scroller.addEventListener('touchcancel', () => { verticalGesture = false; }, { passive: true });
    });

    document.querySelectorAll('.jol-mobile-nav').forEach((details) => {
      details.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', () => {
          if (window.matchMedia?.('(max-width:760px)')?.matches) window.setTimeout(() => details.removeAttribute('open'), 40);
          else details.removeAttribute('open');
        });
      });
    });
  });
})();


// Native splash gate. On the homepage it reveals the store in place.
// If the section is accidentally added to another template (for example 404),
// the same click performs the glow transition and then returns to the storefront root.
document.addEventListener('DOMContentLoaded', () => {
  const gate = document.querySelector('[data-jol-splash]');
  const enter = gate?.querySelector('[data-jol-enter]');
  if (!gate || !enter) return;

  const normalizePath = (value) => {
    const path = (value || '/').split('?')[0].replace(/\/+$/, '');
    return path || '/';
  };

  enter.addEventListener('click', () => {
    if (gate.classList.contains('is-entering')) return;
    gate.classList.add('is-entering');

    const root = gate.dataset.jolRoot || '/';
    const currentPath = normalizePath(window.location.pathname);
    const rootPath = normalizePath(root);
    const isHome = currentPath === rootPath || (rootPath === '/' && currentPath === '/');

    window.setTimeout(() => {
      if (!isHome) {
        window.location.assign(root);
        return;
      }

      try { window.sessionStorage.setItem('jolSplashEntered', '1'); } catch (e) {}
      const storeChrome = document.querySelector('[data-jol-homepage]')?.dataset.jolBrowserChromeColor || '#1f120a';
      window.jolSetBrowserChrome?.('store', storeChrome);
      document.documentElement.classList.add('jol-entered-session');
      document.body.classList.add('jol-entered');
      window.setTimeout(() => {
        gate.setAttribute('aria-hidden', 'true');
      }, 760);
    }, 520);
  });
});

// Donation / gifting anonymity helper.
document.addEventListener('change', (event) => {
  const checkbox = event.target.closest?.('[data-jol-anonymous]');
  if (!checkbox) return;
  const form = checkbox.closest('form');
  const nameField = form?.querySelector('[data-jol-name]');
  if (!nameField) return;
  if (checkbox.checked) {
    nameField.dataset.previousValue = nameField.value;
    nameField.value = '';
    nameField.placeholder = 'Anonymous';
  } else {
    nameField.value = nameField.dataset.previousValue || '';
    nameField.placeholder = 'Name or leave blank if anonymous';
  }
});

// v8: $1 donation product -> quantity equals whole-dollar donation amount.
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.jol-donation-form').forEach((form) => {
    const amount = form.querySelector('[data-jol-donation-amount]');
    const total = form.querySelector('[data-jol-donation-total]');
    const donationProperty = form.querySelector('[data-jol-donation-property]');
    if (!amount) return;

    const sync = () => {
      let value = Math.max(1, Math.floor(Number(amount.value) || 1));
      amount.value = String(value);
      if (total) total.textContent = `$${value}`;
      if (donationProperty) donationProperty.value = `$${value}`;
      form.querySelectorAll('[data-jol-donation-chip]').forEach((chip) => {
        chip.classList.toggle('is-selected', Number(chip.dataset.jolDonationChip) === value);
      });
    };

    amount.addEventListener('input', sync);
    amount.addEventListener('change', sync);
    form.querySelectorAll('[data-jol-donation-chip]').forEach((chip) => {
      chip.addEventListener('click', () => {
        amount.value = chip.dataset.jolDonationChip;
        sync();
        amount.focus({ preventScroll: true });
      });
    });
    form.addEventListener('submit', sync);
    sync();
  });
});

// v13 — product quick view and AJAX bag preview.
(() => {
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const money = (cents) => {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try { return new Intl.NumberFormat(undefined, { style:'currency', currency }).format((Number(cents)||0)/100); }
    catch(e) { return `$${((Number(cents)||0)/100).toFixed(2)}`; }
  };
  const escapeHtml = (value='') => String(value).replace(/[&<>'"]/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;'}[c]));
  const route = (path) => `${ROOT}${path}`.replace(/\/\/{2,}/g,'/');
  const fitInfo = (value='') => {
    const key = String(value).trim().toLowerCase();
    if (key.includes('relaxed')) return { title:'Relaxed Fit', copy:'Roomier through the chest and body with an easy drape and a slightly more laid-back silhouette.' };
    if (key.includes('fitted') || key === 'fit') return { title:'Fitted', copy:'Closer through the chest and sleeves with a cleaner, more tailored shape without feeling skin-tight.' };
    return null;
  };
  let lastFocus = null;
  let quickViewHistoryActive = false;
  let activeProductCard = null;
  let activeProductCardGhost = null;
  let activeProductCardPlaceholder = null;
  let frozenMobileCarousels = [];
  let quickViewAnimating = false;

  const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const isMobileMotion = () => window.matchMedia?.('(max-width: 760px)').matches;
  const motionTimes = () => isMobileMotion()
    ? { card: 230, popup: 290 }
    : { card: 300, popup: 360 };
  const premiumEase = 'cubic-bezier(.22,.78,.20,1)';
  const dropEase = 'cubic-bezier(.42,0,.72,.26)';

  const setLocked = (locked) => document.documentElement.classList.toggle('jol-ui-lock', locked);
  const cardDropDistance = (card) => {
    const rect = card?.getBoundingClientRect?.();
    return rect ? Math.max(window.innerHeight - rect.top + rect.height + 36, window.innerHeight * .72) : window.innerHeight + 160;
  };
  const cardRightDistance = (card) => {
    const rect = card?.getBoundingClientRect?.();
    return rect ? Math.max(window.innerWidth - rect.left + rect.width + 36, window.innerWidth * .72) : window.innerWidth + 160;
  };
  const clearCardMotion = (card) => {
    if (!card) return;
    card.getAnimations?.().forEach(animation => animation.cancel());
    card.style.removeProperty('transform');
    card.style.removeProperty('opacity');
    card.style.removeProperty('translate');
    card.style.removeProperty('will-change');
  };
  const clearCardGhost = () => {
    if (!activeProductCardGhost) return;
    activeProductCardGhost.getAnimations?.().forEach(animation => animation.cancel());
    activeProductCardGhost.remove();
    activeProductCardGhost = null;
  };
  const makeCardGhost = (card, rectOverride=null) => {
    if (!card) return null;
    clearCardGhost();
    const rect = rectOverride || card.getBoundingClientRect();
    const ghost = card.cloneNode(true);
    ghost.classList.add('jol-card-motion-ghost');
    ghost.setAttribute('aria-hidden', 'true');
    ghost.style.setProperty('position','fixed','important');
    ghost.style.setProperty('display','block','important');
    ghost.style.setProperty('visibility','visible','important');
    ghost.style.setProperty('opacity','1','important');
    ghost.style.left = `${rect.left}px`;
    ghost.style.top = `${rect.top}px`;
    ghost.style.width = `${rect.width}px`;
    ghost.style.height = `${rect.height}px`;
    ghost.style.margin = '0';
    ghost.style.pointerEvents = 'none';
    ghost.style.zIndex = '2147482000';
    ghost.style.transformOrigin = '50% 50%';
    ghost.style.setProperty('transform','none','important');
    ghost.style.removeProperty('translate');
    ghost.style.willChange = 'translate, opacity';
    document.body.appendChild(ghost);
    activeProductCardGhost = ghost;
    return ghost;
  };
  const isMobileHomepageCarouselCard = card => !!(isMobileMotion() && card?.closest?.('.jol-merch__scroll-mobile'));
  const freezeMobileCarousels = () => {
    if (!isMobileMotion() || frozenMobileCarousels.length) return;
    frozenMobileCarousels = [...document.querySelectorAll('.jol-merch__scroll-mobile')].map(el => {
      const state = {
        el,
        scrollLeft:el.scrollLeft,
        overflowX:el.style.overflowX,
        scrollSnapType:el.style.scrollSnapType,
        touchAction:el.style.touchAction,
        scrollBehavior:el.style.scrollBehavior,
        overscrollBehaviorInline:el.style.overscrollBehaviorInline,
        pointerEvents:el.style.pointerEvents
      };
      el.classList.add('jol-carousel-transition-frozen');
      el.style.setProperty('overflow-x','hidden','important');
      el.style.setProperty('scroll-snap-type','none','important');
      el.style.setProperty('touch-action','none','important');
      el.style.setProperty('scroll-behavior','auto','important');
      el.style.setProperty('overscroll-behavior-inline','none','important');
      el.style.setProperty('pointer-events','none','important');
      el.scrollLeft = state.scrollLeft;
      return state;
    });
    document.documentElement.classList.add('jol-mobile-carousel-transition');
  };
  const unfreezeMobileCarousels = () => {
    frozenMobileCarousels.forEach(state => {
      const {el} = state;
      el.classList.remove('jol-carousel-transition-frozen');
      const restore = (prop, value) => value ? el.style.setProperty(prop, value) : el.style.removeProperty(prop);
      restore('overflow-x', state.overflowX);
      restore('scroll-snap-type', state.scrollSnapType);
      restore('touch-action', state.touchAction);
      restore('scroll-behavior', state.scrollBehavior);
      restore('overscroll-behavior-inline', state.overscrollBehaviorInline);
      restore('pointer-events', state.pointerEvents);
      el.scrollLeft = state.scrollLeft;
    });
    frozenMobileCarousels = [];
    document.documentElement.classList.remove('jol-mobile-carousel-transition');
  };
  // V150: replace the selected carousel card in-place instead of inserting an extra sibling.
  // Homepage rails use :nth-child() visibility limits (4 Featured/LJ Supply, 8 Graphics);
  // inserting a placeholder changed the following card's index and made the end of the rail collapse.
  const clearCardPlaceholder = () => {
    activeProductCardPlaceholder?.remove();
    activeProductCardPlaceholder = null;
  };
  const restoreCardFromPlaceholder = card => {
    const placeholder = activeProductCardPlaceholder;
    if (!card || !placeholder) return false;
    placeholder.replaceWith(card);
    activeProductCardPlaceholder = null;
    return true;
  };
  const makeCardPlaceholder = card => {
    if (!card?.parentNode) return null;
    clearCardPlaceholder();
    const rect = card.getBoundingClientRect();
    const placeholder = card.cloneNode(false);
    placeholder.removeAttribute('id');
    placeholder.removeAttribute('data-jol-product-card');
    placeholder.removeAttribute('data-jol-product-open');
    placeholder.className = `${card.className} jol-card-transition-placeholder`;
    placeholder.setAttribute('aria-hidden','true');
    placeholder.style.setProperty('box-sizing','border-box','important');
    placeholder.style.setProperty('width',`${rect.width}px`,'important');
    placeholder.style.setProperty('height',`${rect.height}px`,'important');
    placeholder.style.setProperty('min-width',`${rect.width}px`,'important');
    placeholder.style.setProperty('max-width',`${rect.width}px`,'important');
    placeholder.style.setProperty('min-height',`${rect.height}px`,'important');
    placeholder.style.setProperty('max-height',`${rect.height}px`,'important');

    // V225: the homepage deck is vertical on mobile. The old horizontal-rail
    // placeholder used the card WIDTH as its flex-basis; in a column flexbox that
    // becomes the placeholder HEIGHT, leaving only ~60% of the card footprint and
    // making the deck jump when a card leaves/returns. Match the active flex axis.
    const parentStyle = card.parentElement ? getComputedStyle(card.parentElement) : null;
    const verticalFlex = !!parentStyle && parentStyle.display.includes('flex') && parentStyle.flexDirection.startsWith('column');
    const placeholderBasis = verticalFlex ? rect.height : rect.width;
    placeholder.style.setProperty('flex',`0 0 ${placeholderBasis}px`,'important');
    placeholder.style.setProperty('visibility','hidden','important');
    placeholder.style.setProperty('pointer-events','none','important');
    placeholder.style.setProperty('margin',getComputedStyle(card).margin,'important');

    // Keep the exact child index and total rail width stable while the real card is detached.
    card.replaceWith(placeholder);
    activeProductCardPlaceholder = placeholder;
    return placeholder;
  };
  const restoreActiveCardImmediate = () => {
    clearCardGhost();
    if (activeProductCard) {
      clearCardMotion(activeProductCard);
      activeProductCard.style.removeProperty('display');
      activeProductCard.style.removeProperty('visibility');
      activeProductCard.removeAttribute('aria-hidden');
      if (!restoreCardFromPlaceholder(activeProductCard)) {
        clearCardPlaceholder();
      }
    } else {
      clearCardPlaceholder();
    }
    unfreezeMobileCarousels();
    activeProductCard = null;
  };
  const resetCardTransitionState = () => {
    quickViewAnimating = false;
    restoreActiveCardImmediate();
  };
  const animateCardOut = async (card) => {
    if (!card) return;
    activeProductCard = card;
    clearCardGhost();
    const carouselSafe = isMobileHomepageCarouselCard(card);
    if (carouselSafe) {
      freezeMobileCarousels();
      const rect = card.getBoundingClientRect();
      const ghost = makeCardGhost(card, rect);
      makeCardPlaceholder(card);
      card.setAttribute('aria-hidden','true');
      if (prefersReducedMotion()) { clearCardGhost(); return; }
      const distance = Math.max(window.innerHeight - rect.top + rect.height + 36, window.innerHeight * .72);
      const animation = ghost.animate([
        { translate:'0 0', opacity:1, offset:0 },
        { translate:`0 ${Math.round(distance * .72)}px`, opacity:1, offset:.76 },
        { translate:`0 ${distance}px`, opacity:.10, offset:1 }
      ], { duration:motionTimes().card, easing:dropEase, fill:'forwards' });
      await animation.finished.catch(() => {});
      clearCardGhost();
      return;
    }
    if (prefersReducedMotion()) { card.style.visibility = 'hidden'; return; }
    const distance = cardDropDistance(card);
    card.style.willChange = 'transform, opacity';
    const animation = card.animate([
      { translate:'0 0', opacity:1, offset:0 },
      { translate:`0 ${Math.round(distance * .72)}px`, opacity:1, offset:.76 },
      { translate:`0 ${distance}px`, opacity:.10, offset:1 }
    ], { duration:motionTimes().card, easing:dropEase, fill:'forwards' });
    await animation.finished.catch(() => {});
    card.style.visibility = 'hidden';
    card.setAttribute('aria-hidden','true');
    clearCardMotion(card);
  };
  const animateCardBack = async (direction='down') => {
    const card = activeProductCard;
    if (!card) { unfreezeMobileCarousels(); return; }
    const carouselSafe = !!activeProductCardPlaceholder && isMobileMotion();
    if (carouselSafe) {
      const targetRect = activeProductCardPlaceholder.getBoundingClientRect();
      if (!prefersReducedMotion()) {
        const ghost = makeCardGhost(card, targetRect);
        const distance = direction === 'right'
          ? Math.max(window.innerWidth - targetRect.left + targetRect.width + 36, window.innerWidth * .72)
          : Math.max(window.innerHeight - targetRect.top + targetRect.height + 36, window.innerHeight * .72);
        const keyframes = direction === 'right'
          ? [
              { translate:`${distance}px 0`, opacity:.10 },
              { translate:'0 0', opacity:1 }
            ]
          : [
              { translate:`0 ${distance}px`, opacity:.10 },
              { translate:'0 0', opacity:1 }
            ];
        const animation = ghost.animate(keyframes, { duration:motionTimes().card, easing:premiumEase, fill:'forwards' });
        await animation.finished.catch(() => {});
        clearCardGhost();
      }
      card.style.removeProperty('display');
      card.style.removeProperty('visibility');
      card.removeAttribute('aria-hidden');
      restoreCardFromPlaceholder(card);
      unfreezeMobileCarousels();
      activeProductCard = null;
      return;
    }
    clearCardGhost();
    if (prefersReducedMotion()) { restoreActiveCardImmediate(); return; }
    const keyframes = direction === 'right'
      ? [
          { translate:`${cardRightDistance(card)}px 0`, opacity:.10 },
          { translate:'0 0', opacity:1 }
        ]
      : [
          { translate:`0 ${cardDropDistance(card)}px`, opacity:.10 },
          { translate:'0 0', opacity:1 }
        ];
    card.style.visibility = 'visible';
    card.removeAttribute('aria-hidden');
    card.style.willChange = 'transform, opacity';
    const animation = card.animate(keyframes, { duration:motionTimes().card, easing:premiumEase, fill:'forwards' });
    await animation.finished.catch(() => {});
    clearCardMotion(card);
    activeProductCard = null;
  };
  const nextPaint = () => new Promise(resolve => requestAnimationFrame(() => resolve()));
  const animatePopupIn = async (overlay) => {
    const popup = overlay?.querySelector('.jol-quickview');
    if (!popup) return;
    overlay.classList.add('jol-qv-sequencing');
    if (prefersReducedMotion()) {
      overlay.classList.add('is-open');
      overlay.classList.remove('jol-qv-sequencing');
      return;
    }

    // Always establish the exact off-screen first frame before exposing the overlay.
    // This makes the first mobile opening use the same already-composited path as later openings.
    const popupRect = popup.getBoundingClientRect();
    const distance = window.innerHeight + popupRect.height * .55 + 60;
    popup.style.translate = `0 ${distance}px`;
    popup.style.opacity = '.12';
    popup.style.willChange = 'translate, opacity';
    void popup.offsetHeight;
    overlay.classList.add('is-open');
    await nextPaint();
    await nextPaint();

    const animation = popup.animate([
      { translate:`0 ${distance}px`, opacity:.12 },
      { translate:'0 0', opacity:1 }
    ], { duration:motionTimes().popup, easing:premiumEase, fill:'both' });
    await animation.finished.catch(() => {});
    popup.style.removeProperty('translate');
    popup.style.removeProperty('opacity');
    popup.style.removeProperty('will-change');
    overlay.classList.remove('jol-qv-sequencing');
  };
  const animatePopupOut = async (overlay, direction='down') => {
    const popup = overlay?.querySelector('.jol-quickview');
    if (!popup) return;
    overlay.classList.add('jol-qv-sequencing');
    if (!prefersReducedMotion()) {
      const keyframes = direction === 'right'
        ? [
            { translate:'0 0', opacity:1 },
            { translate:`${window.innerWidth + popup.getBoundingClientRect().width * .55 + 60}px 0`, opacity:.08 }
          ]
        : [
            { translate:'0 0', opacity:1 },
            { translate:`0 ${window.innerHeight + popup.getBoundingClientRect().height * .55 + 60}px`, opacity:.08 }
          ];
      const animation = popup.animate(keyframes, { duration:motionTimes().popup, easing:dropEase, fill:'both' });
      await animation.finished.catch(() => {});
    }
    overlay.classList.remove('is-open');
    popup.style.removeProperty('translate');
    popup.style.removeProperty('opacity');
    overlay.classList.remove('jol-qv-sequencing');
  };

  const closeAll = () => {
    document.querySelector('[data-jol-qv-zoom-overlay]')?._jolCloseZoom?.({restoreFocus:false, immediate:true});
    document.querySelectorAll('.jol-overlay.is-open').forEach(el => el.classList.remove('is-open','jol-transition-busy','jol-transition-dimming'));
    restoreActiveCardImmediate();
    setLocked(false);
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus({preventScroll:true});
    document.dispatchEvent(new CustomEvent('jol:ui-neutral'));
  };
  const closeQuickView = async (direction='down', {skipHistory=false, immediate=false}={}) => {
    if (quickViewAnimating && !immediate) return;
    const overlay = document.querySelector('[data-jol-quickview-overlay]');
    if (!overlay?.classList.contains('is-open')) { restoreActiveCardImmediate(); return; }
    overlay._jolResetQuickViewGesture?.();
    overlay._jolResetMediaDrag?.();
    overlay._jolCloseQuickViewZoom?.({restoreFocus:false, immediate:true});
    quickViewAnimating = true;
    const shouldPopHistory = quickViewHistoryActive && !skipHistory;
    quickViewHistoryActive = false;
    if (immediate || prefersReducedMotion()) {
      overlay.classList.remove('is-open');
      restoreActiveCardImmediate();
    } else {
      await animatePopupOut(overlay, direction);
      await animateCardBack(direction);
    }
    setLocked(false);
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus({preventScroll:true});
    document.dispatchEvent(new CustomEvent('jol:ui-neutral'));
    quickViewAnimating = false;
    if (shouldPopHistory) {
      window.__jolSuppressQuickViewPopstate = true;
      history.back();
    }
  };

  const closeQuickViewAfterAdd = async (overlay) => {
    if (!overlay?.classList.contains('is-open')) {
      resetCardTransitionState();
      return;
    }
    overlay._jolResetQuickViewGesture?.();
    overlay._jolResetMediaDrag?.();
    overlay._jolCloseQuickViewZoom?.({restoreFocus:false, immediate:true});
    quickViewAnimating = true;
    const shouldPopHistory = quickViewHistoryActive;
    quickViewHistoryActive = false;
    if (prefersReducedMotion()) {
      overlay.classList.remove('is-open');
      restoreActiveCardImmediate();
    } else {
      await animatePopupOut(overlay, 'right');
      await animateCardBack('right');
    }
    setLocked(false);
    quickViewAnimating = false;
    if (shouldPopHistory) {
      window.__jolSuppressQuickViewPopstate = true;
      await new Promise((resolve) => {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          window.removeEventListener('popstate', finish);
          resolve();
        };
        window.addEventListener('popstate', finish, { once:true });
        history.back();
        window.setTimeout(finish, 180);
      });
    }
  };

  const ensureQuickView = () => {
    let overlay = document.querySelector('[data-jol-quickview-overlay]');
    if (overlay) return overlay;
    overlay = document.createElement('div');
    overlay.className = 'jol-overlay';
    overlay.dataset.jolQuickviewOverlay = '';
    overlay.innerHTML = `
      <div class="jol-quickview" role="dialog" aria-modal="true" aria-labelledby="jol-quickview-title">
        <button class="jol-quickview__close" type="button" data-jol-overlay-close aria-label="Close">×</button>
        <div class="jol-quickview__media" data-jol-qv-media>
          <img class="jol-quickview__image" data-jol-qv-image alt="" draggable="false">
          <img class="jol-quickview__image jol-quickview__image--drag" data-jol-qv-drag-image alt="" aria-hidden="true" draggable="false">
          <button class="jol-quickview__zoom-trigger" type="button" data-jol-qv-zoom-trigger aria-label="Zoom product image">
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.5" cy="10.5" r="5.5"></circle><path d="M15 15l5 5M10.5 8v5M8 10.5h5"></path></svg>
          </button>
          <button class="jol-quickview__media-nav jol-quickview__media-nav--prev" type="button" data-jol-qv-prev aria-label="Previous product image" hidden>‹</button>
          <button class="jol-quickview__media-nav jol-quickview__media-nav--next" type="button" data-jol-qv-next aria-label="Next product image" hidden>›</button>
          <div class="jol-quickview__media-count" data-jol-qv-media-count hidden></div>
        </div>
        <div class="jol-quickview__info">
          <p class="jol-quickview__eyebrow" data-jol-qv-type>Luck Jar</p>
          <h2 class="jol-quickview__title" id="jol-quickview-title" data-jol-qv-title></h2>
          <p class="jol-quickview__price" data-jol-qv-price></p>
          <div class="jol-quickview__description" data-jol-qv-description></div>
          <div class="jol-quickview__options" data-jol-qv-options></div>
          <button class="jol-fit-guide__trigger jol-fit-guide__trigger--quickview" type="button" data-jol-fit-guide-trigger data-jol-qv-fit-guide hidden><span class="jol-fit-guide__trigger-main">See how it fits</span><span class="jol-fit-guide__trigger-icon" aria-hidden="true">→</span></button>
          <button class="jol-quickview__add" type="button" data-jol-qv-add>
            <span class="jol-quickview__add-label" data-jol-qv-add-label>Add to Bag</span>
            <span class="jol-quickview__add-success" aria-hidden="true">
              <svg class="jol-quickview__checkmark-burst" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g class="check"><circle class="ring" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/><path class="tick" d="M9 12.75L11.25 15L15 9.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></g>
                <g class="burst">
                  <g style="--index:0"><line class="line" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor" stroke-linecap="round"/></g>
                  <g style="--index:1"><line class="line" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor" stroke-linecap="round"/></g>
                  <g style="--index:2"><line class="line" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor" stroke-linecap="round"/></g>
                  <g style="--index:3"><line class="line" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor" stroke-linecap="round"/></g>
                  <g style="--index:4"><line class="line" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor" stroke-linecap="round"/></g>
                  <g style="--index:5"><line class="line" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor" stroke-linecap="round"/></g>
                  <g style="--index:6"><line class="line" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor" stroke-linecap="round"/></g>
                  <g style="--index:7"><line class="line" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor" stroke-linecap="round"/></g>
                </g>
              </svg>
            </span>
          </button>
          <div class="jol-quickview__status" data-jol-qv-status aria-live="polite"></div>
          <a class="jol-quickview__details" data-jol-qv-details href="#">View full details</a>
        </div>
      </div>`;
    document.body.appendChild(overlay);
    overlay.addEventListener('click', e => {
      if (e.target === overlay || e.target.closest('[data-jol-overlay-close]')) closeQuickView('down');
    });

    const fullDetailsLink = overlay.querySelector('[data-jol-qv-details]');
    fullDetailsLink?.addEventListener('click', e => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
      fullDetailsLink.setAttribute('data-view-transition-triggered', '');
      fullDetailsLink.setAttribute('data-view-transition-type', 'jol-full-details');
      try {
        sessionStorage.setItem('jolFullDetailsEnter', '1');
        sessionStorage.setItem('jolFullDetailsOrigin', JSON.stringify({
          url: `${location.pathname}${location.search}`,
          time: Date.now()
        }));
      } catch (_) {}
    });

    // Product media keeps explicit arrows. A rightward swipe now triggers the physical-card close gesture.
    const prev = overlay.querySelector('[data-jol-qv-prev]');
    const next = overlay.querySelector('[data-jol-qv-next]');
    prev?.addEventListener('click', e => { e.preventDefault(); e.stopPropagation(); overlay._jolGalleryPrev?.(); });
    next?.addEventListener('click', e => { e.preventDefault(); e.stopPropagation(); overlay._jolGalleryNext?.(); });

    const panel = overlay.querySelector('.jol-quickview');
    let swipeStartX = 0, swipeStartY = 0, swipePointer = null;
    let wheelX = 0, wheelReset = 0;
    const gestureBlocked = target => target instanceof Element && !!target.closest('button,a,input,select,textarea,label,.jol-quickview__option-values,[data-jol-qv-media]');
    const resetQuickViewGesture = () => {
      swipeStartX = 0;
      swipeStartY = 0;
      swipePointer = null;
      wheelX = 0;
      window.clearTimeout(wheelReset);
      wheelReset = 0;
    };
    overlay._jolResetQuickViewGesture = resetQuickViewGesture;
    panel?.addEventListener('pointerdown', e => {
      if (gestureBlocked(e.target) || quickViewAnimating || !overlay.classList.contains('is-open')) return;
      swipeStartX = e.clientX;
      swipeStartY = e.clientY;
      swipePointer = e.pointerId;
    });
    document.addEventListener('pointerup', e => {
      if (swipePointer !== e.pointerId) return;
      const dx = e.clientX - swipeStartX;
      const dy = e.clientY - swipeStartY;
      resetQuickViewGesture();
      if (!overlay.classList.contains('is-open') || quickViewAnimating) return;
      if (dx > 72 && Math.abs(dx) > Math.abs(dy) * 1.18) closeQuickView('right');
    });
    document.addEventListener('pointercancel', e => {
      if (swipePointer === e.pointerId) resetQuickViewGesture();
    });

    // V151: popup media stays simple. A tap/click opens an isolated full-screen zoom viewer;
    // one-finger horizontal swipes still change gallery images inside the popup.
    const media = overlay.querySelector('[data-jol-qv-media]');
    const mediaImage = overlay.querySelector('[data-jol-qv-image]');
    const zoomTrigger = overlay.querySelector('[data-jol-qv-zoom-trigger]');

    const ensureZoomViewer = () => {
      let viewer = document.querySelector('[data-jol-qv-zoom-overlay]');
      if (viewer) return viewer;
      viewer = document.createElement('div');
      viewer.className = 'jol-qv-zoom';
      viewer.dataset.jolQvZoomOverlay = '';
      viewer.setAttribute('role','dialog');
      viewer.setAttribute('aria-modal','true');
      viewer.setAttribute('aria-label','Product image zoom');
      viewer.hidden = true;
      viewer.setAttribute('aria-hidden','true');
      viewer.innerHTML = `
        <button class="jol-qv-zoom__close" type="button" data-jol-qv-zoom-close aria-label="Close image zoom">×</button>
        <div class="jol-qv-zoom__level" data-jol-qv-zoom-level aria-hidden="true">1×</div>
        <img class="jol-qv-zoom__image" data-jol-qv-zoom-image alt="" draggable="false">`;
      document.body.appendChild(viewer);

      const zoomImage = viewer.querySelector('[data-jol-qv-zoom-image]');
      const zoomLevel = viewer.querySelector('[data-jol-qv-zoom-level]');
      const closeButton = viewer.querySelector('[data-jol-qv-zoom-close]');
      const pointers = new Map();
      let scale = 1, translateX = 0, translateY = 0;
      let pinchStartDistance = 0, pinchStartScale = 1;
      let lastTapTime = 0;
      let singlePointerStart = null;
      let moved = false;
      let lastZoomFocus = null;
      let closeTimer = 0;
      const clampScale = value => Math.max(1, Math.min(3, value));
      const pointerDistance = () => {
        const pts = [...pointers.values()];
        if (pts.length < 2) return 0;
        return Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
      };
      const pointerMidpoint = () => {
        const pts = [...pointers.values()];
        if (pts.length < 2) return {x:window.innerWidth/2,y:window.innerHeight/2};
        return {x:(pts[0].x + pts[1].x)/2,y:(pts[0].y + pts[1].y)/2};
      };
      const clampPan = () => {
        if (!zoomImage) return;
        const baseW = zoomImage.offsetWidth || 0;
        const baseH = zoomImage.offsetHeight || 0;
        const maxX = Math.max(0, (baseW * scale - window.innerWidth) / 2 + 22);
        const maxY = Math.max(0, (baseH * scale - window.innerHeight) / 2 + 22);
        translateX = Math.max(-maxX, Math.min(maxX, translateX));
        translateY = Math.max(-maxY, Math.min(maxY, translateY));
        if (scale <= 1.001) { translateX = 0; translateY = 0; }
      };
      const renderZoom = (animate=false) => {
        clampPan();
        zoomImage.style.transition = animate ? 'transform .18s cubic-bezier(.22,.78,.20,1)' : 'none';
        zoomImage.style.transform = `translate3d(${translateX}px,${translateY}px,0) scale(${scale})`;
        viewer.classList.toggle('is-zoomed', scale > 1.01);
        if (zoomLevel) zoomLevel.textContent = `${Number(scale.toFixed(1))}×`;
      };
      const resetZoom = (animate=false) => {
        pointers.clear();
        scale = 1; translateX = 0; translateY = 0;
        pinchStartDistance = 0; pinchStartScale = 1;
        singlePointerStart = null; moved = false;
        viewer.classList.remove('is-dragging');
        renderZoom(animate);
      };
      const setScaleAt = (nextScale, clientX, clientY, animate=false) => {
        const next = clampScale(nextScale);
        const cx = window.innerWidth / 2;
        const cy = window.innerHeight / 2;
        const localX = (clientX - cx - translateX) / Math.max(scale, .001);
        const localY = (clientY - cy - translateY) / Math.max(scale, .001);
        translateX = clientX - cx - localX * next;
        translateY = clientY - cy - localY * next;
        scale = next;
        renderZoom(animate);
      };
      const closeZoom = ({restoreFocus=true, immediate=false}={}) => {
        window.clearTimeout(closeTimer);
        pointers.clear();
        viewer.classList.remove('is-open','is-dragging');
        viewer.setAttribute('aria-hidden','true');
        document.documentElement.classList.remove('jol-qv-zoom-open');
        const finish = () => {
          if (!viewer.classList.contains('is-open')) viewer.hidden = true;
          resetZoom(false);
          if (restoreFocus && lastZoomFocus && typeof lastZoomFocus.focus === 'function') lastZoomFocus.focus({preventScroll:true});
        };
        if (immediate || prefersReducedMotion()) finish();
        else closeTimer = window.setTimeout(finish, 180);
      };
      const openZoom = (sourceImage=mediaImage) => {
        if (!sourceImage?.src || quickViewAnimating) return;
        window.clearTimeout(closeTimer);
        lastZoomFocus = document.activeElement;
        resetZoom(false);
        zoomImage.src = sourceImage.currentSrc || sourceImage.src;
        zoomImage.alt = sourceImage.alt || 'Product image';
        viewer.hidden = false;
        viewer.setAttribute('aria-hidden','false');
        document.documentElement.classList.add('jol-qv-zoom-open');
        requestAnimationFrame(() => {
          viewer.classList.add('is-open');
          closeButton?.focus({preventScroll:true});
        });
      };
      viewer._jolOpenZoom = openZoom;
      viewer._jolCloseZoom = closeZoom;
      viewer._jolResetZoom = resetZoom;

      closeButton?.addEventListener('click', e => { e.preventDefault(); e.stopPropagation(); closeZoom(); });
      viewer.addEventListener('click', e => { if (e.target === viewer) closeZoom(); });
      viewer.addEventListener('wheel', e => {
        if (!viewer.classList.contains('is-open')) return;
        e.preventDefault();
        const factor = Math.exp(-e.deltaY * .0018);
        setScaleAt(scale * factor, e.clientX, e.clientY, false);
      }, {passive:false});
      zoomImage?.addEventListener('dblclick', e => {
        e.preventDefault();
        if (scale > 1.01) resetZoom(true);
        else setScaleAt(2, e.clientX, e.clientY, true);
      });
      zoomImage?.addEventListener('pointerdown', e => {
        if (!viewer.classList.contains('is-open')) return;
        const point = {x:e.clientX,y:e.clientY};
        pointers.set(e.pointerId, point);
        try { zoomImage.setPointerCapture?.(e.pointerId); } catch (_) {}
        if (pointers.size === 1) {
          singlePointerStart = {x:e.clientX,y:e.clientY,id:e.pointerId};
          moved = false;
          if (scale > 1.01) viewer.classList.add('is-dragging');
        } else if (pointers.size === 2) {
          pinchStartDistance = Math.max(1, pointerDistance());
          pinchStartScale = scale;
          singlePointerStart = null;
          moved = true;
          viewer.classList.add('is-dragging');
        }
      });
      zoomImage?.addEventListener('pointermove', e => {
        const previous = pointers.get(e.pointerId);
        if (!previous) return;
        pointers.set(e.pointerId,{x:e.clientX,y:e.clientY});
        if (e.cancelable) e.preventDefault();
        if (pointers.size >= 2) {
          const midpoint = pointerMidpoint();
          const next = pinchStartScale * (pointerDistance() / Math.max(1,pinchStartDistance));
          setScaleAt(next, midpoint.x, midpoint.y, false);
          moved = true;
          return;
        }
        if (singlePointerStart && singlePointerStart.id === e.pointerId) {
          if (Math.hypot(e.clientX-singlePointerStart.x,e.clientY-singlePointerStart.y) > 6) moved = true;
          if (scale > 1.01) {
            translateX += e.clientX - previous.x;
            translateY += e.clientY - previous.y;
            renderZoom(false);
          }
        }
      }, {passive:false});
      const finishZoomPointer = e => {
        if (!pointers.has(e.pointerId)) return;
        const wasMulti = pointers.size > 1;
        pointers.delete(e.pointerId);
        try { zoomImage.releasePointerCapture?.(e.pointerId); } catch (_) {}
        if (pointers.size < 2) { pinchStartDistance = 0; pinchStartScale = scale; }
        if (!pointers.size) viewer.classList.remove('is-dragging');
        if (wasMulti) { singlePointerStart = null; return; }
        if (!singlePointerStart || singlePointerStart.id !== e.pointerId) return;
        const shouldTap = !moved && Math.hypot(e.clientX-singlePointerStart.x,e.clientY-singlePointerStart.y) <= 10;
        singlePointerStart = null;
        if (!shouldTap || e.pointerType !== 'touch') return;
        const now = performance.now();
        if (now - lastTapTime < 320) {
          if (scale > 1.01) resetZoom(true);
          else setScaleAt(2, e.clientX, e.clientY, true);
          lastTapTime = 0;
        } else lastTapTime = now;
      };
      zoomImage?.addEventListener('pointerup', finishZoomPointer);
      zoomImage?.addEventListener('pointercancel', e => {
        pointers.delete(e.pointerId);
        if (!pointers.size) viewer.classList.remove('is-dragging');
        singlePointerStart = null;
      });
      window.addEventListener('resize', () => { if (viewer.classList.contains('is-open')) renderZoom(false); }, {passive:true});
      return viewer;
    };

    const zoomViewer = ensureZoomViewer();
    const openQuickViewZoom = () => zoomViewer?._jolOpenZoom?.(mediaImage);
    const closeQuickViewZoom = (options={restoreFocus:false, immediate:true}) => zoomViewer?._jolCloseZoom?.(options);
    overlay._jolResetQuickViewZoom = () => closeQuickViewZoom({restoreFocus:false, immediate:true});
    overlay._jolCloseQuickViewZoom = closeQuickViewZoom;
    zoomTrigger?.addEventListener('click', e => { e.preventDefault(); e.stopPropagation(); openQuickViewZoom(); });

    const mediaPointers = new Map();
    const dragImage = overlay.querySelector('[data-jol-qv-drag-image]');
    let mediaStartX = 0, mediaStartY = 0, mediaPointer = null;
    let mediaDragDx = 0, mediaDragPreviewIndex = null, mediaDragDirection = 0;
    let mediaDragging = false, mediaSettling = false;

    const galleryState = () => overlay._jolGalleryState?.() || { index:0, images:[] };
    const galleryWrapIndex = (index, count) => count ? (index + count) % count : 0;
    const setMediaDragPosition = (currentX, previewX) => {
      media?.style.setProperty('--jol-qv-drag-current-x', `${currentX}px`);
      media?.style.setProperty('--jol-qv-drag-preview-x', `${previewX}px`);
    };
    const clearMediaDrag = () => {
      mediaDragging = false;
      mediaSettling = false;
      mediaDragDx = 0;
      mediaDragPreviewIndex = null;
      mediaDragDirection = 0;
      media?.classList.remove('is-gallery-dragging','is-gallery-settling');
      media?.style.removeProperty('--jol-qv-drag-current-x');
      media?.style.removeProperty('--jol-qv-drag-preview-x');
      if (dragImage) {
        dragImage.removeAttribute('src');
        dragImage.alt = '';
      }
    };
    overlay._jolResetMediaDrag = clearMediaDrag;

    const prepareMediaPreview = (direction) => {
      const state = galleryState();
      if (!dragImage || state.images.length < 2) return false;
      mediaDragDirection = direction;
      mediaDragPreviewIndex = galleryWrapIndex(state.index + direction, state.images.length);
      dragImage.src = state.images[mediaDragPreviewIndex] || '';
      dragImage.alt = '';
      media?.classList.add('is-gallery-dragging');
      return true;
    };

    const renderMediaDrag = (dx) => {
      const state = galleryState();
      if (!media || state.images.length < 2) return;
      const width = Math.max(1, media.getBoundingClientRect().width);
      const clamped = Math.max(-width, Math.min(width, dx));
      const direction = clamped < 0 ? 1 : -1;
      if (!mediaDragging || direction !== mediaDragDirection) {
        if (!prepareMediaPreview(direction)) return;
      }
      mediaDragging = true;
      mediaDragDx = clamped;
      const previewStart = direction > 0 ? width : -width;
      setMediaDragPosition(clamped, clamped + previewStart);
    };

    const settleMediaDrag = (commit) => {
      if (!media || !mediaDragging || mediaSettling) return;
      mediaSettling = true;
      const width = Math.max(1, media.getBoundingClientRect().width);
      const direction = mediaDragDirection || (mediaDragDx < 0 ? 1 : -1);
      const previewStart = direction > 0 ? width : -width;
      media.classList.remove('is-gallery-dragging');
      media.classList.add('is-gallery-settling');
      requestAnimationFrame(() => {
        if (commit) setMediaDragPosition(direction > 0 ? -width : width, 0);
        else setMediaDragPosition(0, previewStart);
      });
      window.setTimeout(() => {
        if (commit && mediaDragPreviewIndex != null) overlay._jolGalleryCommit?.(mediaDragPreviewIndex);
        clearMediaDrag();
      }, prefersReducedMotion() ? 0 : 205);
    };

    media?.addEventListener('pointerdown', e => {
      if (e.target instanceof Element && e.target.closest('button')) return;
      if (mediaSettling) return;
      mediaPointers.set(e.pointerId,{x:e.clientX,y:e.clientY});
      if (mediaPointers.size !== 1) { mediaPointer = null; clearMediaDrag(); return; }
      mediaStartX = e.clientX;
      mediaStartY = e.clientY;
      mediaPointer = e.pointerId;
      mediaDragDx = 0;
      try { media.setPointerCapture?.(e.pointerId); } catch (_) {}
    });

    media?.addEventListener('pointermove', e => {
      if (mediaPointer !== e.pointerId || !mediaPointers.has(e.pointerId) || mediaSettling) return;
      mediaPointers.set(e.pointerId,{x:e.clientX,y:e.clientY});
      const dx = e.clientX - mediaStartX;
      const dy = e.clientY - mediaStartY;
      if (!mediaDragging) {
        if (Math.abs(dx) < 8) return;
        if (Math.abs(dx) <= Math.abs(dy) * 1.05) return;
      }
      if (galleryState().images.length < 2) return;
      if (e.cancelable) e.preventDefault();
      renderMediaDrag(dx);
    }, {passive:false});

    const finishMediaPointer = e => {
      if (!mediaPointers.has(e.pointerId)) return;
      mediaPointers.delete(e.pointerId);
      try { media.releasePointerCapture?.(e.pointerId); } catch (_) {}
      if (mediaPointer !== e.pointerId) { if (!mediaPointers.size) mediaPointer = null; return; }
      const dx = e.clientX - mediaStartX;
      const dy = e.clientY - mediaStartY;
      const distance = Math.hypot(dx,dy);
      mediaPointer = null;

      if (mediaDragging) {
        const width = Math.max(1, media?.getBoundingClientRect?.().width || 1);
        const threshold = Math.min(76, width * .18);
        const commit = Math.abs(mediaDragDx) >= threshold && Math.abs(mediaDragDx) > Math.abs(dy) * 1.04;
        settleMediaDrag(commit);
        return;
      }
      if (distance <= 12 && mediaImage) openQuickViewZoom();
    };
    media?.addEventListener('pointerup', finishMediaPointer);
    media?.addEventListener('pointercancel', e => {
      mediaPointers.delete(e.pointerId);
      if (mediaPointer === e.pointerId) mediaPointer = null;
      clearMediaDrag();
    });


    // Trackpad horizontal swipe support on desktop; normal vertical scrolling remains untouched.
    panel?.addEventListener('wheel', e => {
      if (gestureBlocked(e.target) || quickViewAnimating || !overlay.classList.contains('is-open')) return;
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY) * 1.15) return;
      wheelX += e.deltaX;
      window.clearTimeout(wheelReset);
      wheelReset = window.setTimeout(() => { wheelX = 0; }, 180);
      if (wheelX < -95) { resetQuickViewGesture(); closeQuickView('right'); }
    }, {passive:true});
    return overlay;
  };

  const ensureCart = () => {
    let overlay = document.querySelector('[data-jol-cart-overlay]');
    if (overlay) return overlay;
    overlay = document.createElement('div');
    overlay.className = 'jol-overlay';
    overlay.dataset.jolCartOverlay = '';
    overlay.innerHTML = `
      <aside class="jol-cart-preview" role="dialog" aria-modal="true" aria-labelledby="jol-cart-title">
        <div class="jol-cart-preview__header"><h2 id="jol-cart-title">Your Bag</h2><button class="jol-cart-preview__close" type="button" data-jol-overlay-close aria-label="Close">×</button></div>
        <div class="jol-cart-preview__body" data-jol-cart-body></div>
        <div class="jol-cart-preview__footer" data-jol-cart-footer></div>
      </aside>`;
    document.body.appendChild(overlay);
    overlay.addEventListener('click', e => { if (e.target === overlay || e.target.closest('[data-jol-overlay-close]')) closeAll(); });
    overlay.addEventListener('click', async e => {
      const btn = e.target.closest('[data-jol-cart-change]');
      if (!btn) return;
      const line = Number(btn.dataset.line);
      const quantity = Math.max(0, Number(btn.dataset.quantity));
      btn.disabled = true;
      try {
        await fetch(route('cart/change.js'), { method:'POST', headers:{'Content-Type':'application/json','Accept':'application/json'}, body:JSON.stringify({line, quantity}) });
        await refreshCart();
      } catch(err) { btn.disabled = false; }
    });
    return overlay;
  };

  const updateCartCounts = (count) => document.querySelectorAll('.jol-cart-count').forEach(el => el.textContent = `(${count})`);

  const refreshCart = async () => {
    const overlay = ensureCart();
    const body = overlay.querySelector('[data-jol-cart-body]');
    const footer = overlay.querySelector('[data-jol-cart-footer]');
    const FREE_SHIPPING_THRESHOLD = 8000;
    const isGiveawayItem = (item) => item?.properties?._jol_giveaway_entry === 'true' || item?.handle === 'weekly-giveaway' || /weekly giveaway/i.test(item?.product_title || '');
    body.innerHTML = '<div class="jol-cart-preview__empty">Opening your bag…</div>';
    try {
      const cart = await fetch(route('cart.js'), {headers:{'Accept':'application/json'}}).then(r => r.json());
      const visibleItemCount = cart.items.reduce((sum,item) => sum + (isGiveawayItem(item) ? 1 : Number(item.quantity || 0)), 0);
      updateCartCounts(visibleItemCount);
      if (!cart.items.length) {
        body.innerHTML = '<div class="jol-cart-preview__empty">Your bag is empty.<br>Add something lucky.</div>';
        footer.innerHTML = `<a class="jol-cart-preview__view" href="${route('collections/all')}">Browse the collection</a>`;
        return cart;
      }
      body.innerHTML = cart.items.map((item,i) => {
        const image = item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}">` : '';
        const variant = item.variant_title && item.variant_title !== 'Default Title' ? escapeHtml(item.variant_title) : '';
        const giveawayFlag = isGiveawayItem(item);
        const donationAmount = item.properties?._jol_donation_amount || money(item.original_line_price || item.final_line_price);
        const blankFlag = !giveawayFlag && (item.properties?._jol_bulk_blank === 'true' || /(^|\s)blank(s)?(\s|$)|best blanks|bulk blank/i.test(`${item.product_type || ''} ${item.product_title || ''}`));
        const giftFlag = !giveawayFlag && item.properties?._jol_bulk_gift === 'true';
        const minimum = giftFlag ? 15 : (blankFlag ? 10 : 0);
        const bulkProductKey = String(item.properties?._jol_bulk_product || item.product_id || '');
        const bulkProductTotal = minimum ? cart.items.filter(other => {
          const otherBlank = other.properties?._jol_bulk_blank === 'true' || /(^|\s)blank(s)?(\s|$)|best blanks|bulk blank/i.test(`${other.product_type || ''} ${other.product_title || ''}`);
          const otherGift = other.properties?._jol_bulk_gift === 'true';
          const sameKind = giftFlag ? otherGift : otherBlank;
          return sameKind && String(other.properties?._jol_bulk_product || other.product_id || '') === bulkProductKey;
        }).reduce((sum, other) => sum + Number(other.quantity || 0), 0) : 0;
        const blockDecrease = minimum > 0 && bulkProductTotal <= minimum;
        const controls = giveawayFlag
          ? `<div class="jol-cart-item__controls jol-cart-item__controls--giveaway"><span class="jol-cart-item__entry-count">1 entry</span><button type="button" data-jol-cart-change data-line="${i+1}" data-quantity="0" aria-label="Remove weekly giveaway entry">Remove</button></div>`
          : `<div class="jol-cart-item__controls">
              <button type="button" data-jol-cart-change data-line="${i+1}" data-quantity="${Math.max(0,item.quantity-1)}" ${blockDecrease ? `disabled title="${giftFlag ? 'Gift orders require at least 15 pieces' : 'Blank orders require at least 10 shirts'}"` : ''} aria-label="Decrease quantity">−</button>
              <span class="jol-cart-item__qty">${item.quantity}</span>
              <button type="button" data-jol-cart-change data-line="${i+1}" data-quantity="${item.quantity+1}" aria-label="Increase quantity">+</button>
            </div>`;
        const originalLine = Number(item.original_line_price || item.final_line_price || 0);
        const finalLine = Number(item.final_line_price || 0);
        const priceMarkup = giveawayFlag
          ? `<div class="jol-cart-item__price jol-cart-item__price--donation"><span>Donation</span><strong>${escapeHtml(donationAmount)}</strong></div>`
          : (originalLine > finalLine
              ? `<div class="jol-cart-item__price is-discounted"><s>${money(originalLine)}</s><strong>${money(finalLine)}</strong></div>`
              : `<div class="jol-cart-item__price">${money(finalLine)}</div>`);
        return `<div class="jol-cart-item" data-jol-cart-blank="${blankFlag ? 'true' : 'false'}"${giveawayFlag ? ' data-jol-giveaway-entry="true"' : ''}>
          ${image}
          <div><p class="jol-cart-item__title">${escapeHtml(item.product_title)}</p><p class="jol-cart-item__variant">${variant}</p>
            ${controls}
            ${minimum ? `<p class="jol-cart-item__minimum">${giftFlag ? '15-gift minimum per gift style' : '10-shirt minimum per blank style'}</p>` : ''}
          </div>
          ${priceMarkup}
        </div>`;
      }).join('');
      const invalidBlank = [...new Set(cart.items.filter(item => item.properties?._jol_bulk_blank === 'true' || /(^|\s)blank(s)?(\s|$)|best blanks|bulk blank/i.test(`${item.product_type || ''} ${item.product_title || ''}`)).map(item => String(item.properties?._jol_bulk_product || item.product_id || '')))].some(key => cart.items.filter(item => (item.properties?._jol_bulk_blank === 'true' || /(^|\s)blank(s)?(\s|$)|best blanks|bulk blank/i.test(`${item.product_type || ''} ${item.product_title || ''}`)) && String(item.properties?._jol_bulk_product || item.product_id || '') === key).reduce((sum,item)=>sum+Number(item.quantity||0),0) < 10);
      const invalidGift = [...new Set(cart.items.filter(item => item.properties?._jol_bulk_gift === 'true').map(item => String(item.properties?._jol_bulk_product || item.product_id || '')))].some(key => cart.items.filter(item => item.properties?._jol_bulk_gift === 'true' && String(item.properties?._jol_bulk_product || item.product_id || '') === key).reduce((sum,item)=>sum+Number(item.quantity||0),0) < 15);
      const minimumWarning = invalidBlank || invalidGift ? `<div class="jol-cart-preview__minimum-warning">${invalidBlank ? 'Blank styles require at least 10 shirts each. ' : ''}${invalidGift ? 'Gift styles require at least 15 pieces each.' : ''}</div>` : '';
      const shippingEligibleSubtotal = cart.items.filter(item => !isGiveawayItem(item)).reduce((sum,item) => sum + Number(item.final_line_price || 0), 0);
      const shippingRemaining = Math.max(0, FREE_SHIPPING_THRESHOLD - shippingEligibleSubtotal);
      const shippingProgress = Math.max(0, Math.min(100, Math.round((shippingEligibleSubtotal / FREE_SHIPPING_THRESHOLD) * 100)));
      const shippingMessage = shippingRemaining === 0 ? 'Free shipping unlocked' : `${money(shippingRemaining)} away from free shipping`;
      const shippingMarkup = `<div class="jol-cart-preview__shipping" aria-label="Free shipping progress"><div class="jol-cart-preview__shipping-copy"><strong>Free US Shipping on $80+</strong><span class="${shippingRemaining === 0 ? 'is-unlocked' : ''}">${shippingMessage}</span></div><div class="jol-cart-preview__shipping-track"><i style="width:${shippingProgress}%"></i></div></div>`;
      footer.innerHTML = `<div class="jol-cart-preview__subtotal"><span>Subtotal</span><strong>${money(cart.total_price)}</strong></div>${shippingMarkup}${invalidBlank || invalidGift ? minimumWarning : `<a class="jol-cart-preview__checkout jol-leather jol-stitch" href="${route('cart')}">Checkout</a>`}`;
      return cart;
    } catch(err) {
      body.innerHTML = '<div class="jol-cart-preview__empty">Could not load the bag. You can still open the full cart.</div>';
      footer.innerHTML = `<a class="jol-cart-preview__checkout jol-leather jol-stitch" href="${route('cart')}">Open cart</a>`;
    }
  };

  const openCart = async (trigger) => {
    closeAll();
    lastFocus = trigger || document.activeElement;
    const overlay = ensureCart();
    overlay.classList.add('is-open');
    setLocked(true);
    await refreshCart();
    overlay.querySelector('[data-jol-overlay-close]')?.focus({preventScroll:true});
  };

  const setQuickViewAddLabel = (button, label) => {
    const labelEl = button?.querySelector?.('[data-jol-qv-add-label]');
    if (labelEl) labelEl.textContent = label;
    else if (button) button.textContent = label;
  };

  const animateQuickViewAdded = async (button) => {
    if (!button) return;
    button.dataset.added = 'true';
    await new Promise(resolve => window.setTimeout(resolve, prefersReducedMotion() ? 180 : 760));
  };

  const renderProduct = (product, overlay) => {
    overlay._jolResetMediaDrag?.();
    const image = overlay.querySelector('[data-jol-qv-image]');
    const title = overlay.querySelector('[data-jol-qv-title]');
    const type = overlay.querySelector('[data-jol-qv-type]');
    const price = overlay.querySelector('[data-jol-qv-price]');
    const desc = overlay.querySelector('[data-jol-qv-description]');
    const optionsWrap = overlay.querySelector('[data-jol-qv-options]');
    const fitGuideButton = overlay.querySelector('[data-jol-qv-fit-guide]');
    const details = overlay.querySelector('[data-jol-qv-details]');
    const add = overlay.querySelector('[data-jol-qv-add]');
    const status = overlay.querySelector('[data-jol-qv-status]');
    add?.removeAttribute('data-added');
    overlay.querySelectorAll('.jol-fit-promo--quickview').forEach(el => el.remove());
    const prevImage = overlay.querySelector('[data-jol-qv-prev]');
    const nextImage = overlay.querySelector('[data-jol-qv-next]');
    const imageCount = overlay.querySelector('[data-jol-qv-media-count]');
    const selected = [];
    const blankText = `${product.title || ''} ${product.type || ''} ${Array.isArray(product.tags) ? product.tags.join(' ') : (product.tags || '')}`.toLowerCase();
    const isBlank = product._jolIsBlank === true || /(^|\s)blank(s)?(\s|$)|best blanks|bulk blank/.test(blankText);
    const isGift = product._jolIsGift === true || (!/gift card/.test(blankText) && /(^|\s)gift(s)?(\s|$)|gifts from the jar|bulk gift/.test(blankText));
    overlay.dataset.jolBlank = isBlank ? 'true' : 'false';
    overlay.dataset.jolGift = isGift ? 'true' : 'false';

    title.textContent = product.title || '';
    type.textContent = product.type || 'Luck Jar';
    desc.innerHTML = product.description || '';
    details.href = product.url || route(`products/${encodeURIComponent(product.handle || '')}`);
    // Keep quick-view media 1:1 with the images uploaded in Shopify's product media section.
    // Do not append featured/variant images separately because Shopify already includes uploaded
    // product images in product.images and the extra sources can create duplicate slides/counts.
    const galleryImages = (Array.isArray(product.images) ? product.images : [])
      .map(src => typeof src === 'string' ? src : src?.src)
      .filter(Boolean);
    let galleryIndex = 0;
    const showGalleryImage = (index) => {
      overlay._jolResetQuickViewZoom?.();
      if (!galleryImages.length) { image.removeAttribute('src'); image.alt=''; return; }
      galleryIndex = (index + galleryImages.length) % galleryImages.length;
      image.src = galleryImages[galleryIndex];
      image.alt = `${product.title || ''}${galleryImages.length > 1 ? ` – image ${galleryIndex + 1}` : ''}`;
      if (imageCount) {
        imageCount.textContent = `${galleryIndex + 1} / ${galleryImages.length}`;
        imageCount.hidden = galleryImages.length < 2;
      }
    };
    overlay._jolGalleryPrev = () => { overlay._jolResetMediaDrag?.(); showGalleryImage(galleryIndex - 1); };
    overlay._jolGalleryNext = () => { overlay._jolResetMediaDrag?.(); showGalleryImage(galleryIndex + 1); };
    overlay._jolGalleryState = () => ({ index:galleryIndex, images:galleryImages });
    overlay._jolGalleryCommit = (index) => showGalleryImage(index);
    if (prevImage) prevImage.hidden = galleryImages.length < 2;
    if (nextImage) nextImage.hidden = galleryImages.length < 2;
    showGalleryImage(0);
    // V224: preload popup slides so the neighboring image is already available while dragging.
    galleryImages.forEach((src) => { const preload = new Image(); preload.src = src; });
    if (isBlank) {
      const unitPrice = Number(product.price || product.variants?.[0]?.price || 0);
      price.textContent = `From ${money(unitPrice * 10)} · ${money(unitPrice)}/shirt`;
    } else if (isGift) {
      const unitPrice = Number(product.price || product.variants?.[0]?.price || 0);
      price.textContent = `From ${money(unitPrice * 15)} · 15 minimum`;
    }

    const options = (product.options || []).map((opt, idx) => typeof opt === 'string' ? {name:opt, position:idx+1} : opt);
    const explicitFit = options.some(opt => /fit/i.test(opt.name || '')) || (product.variants || []).some(v => ['fitted','relaxed'].some(word => [v.option1,v.option2,v.option3].some(value => String(value || '').toLowerCase() === word)));
    const searchableProductText = `${product.title || ''} ${product.type || ''} ${(product.tags || []).join(' ')}`.toLowerCase();
    const excludedFitGuideProduct = /gift card|\bsock|sticker|tote|\bbag\b|\bhat\b|\bmug\b/.test(searchableProductText);
    const looksLikeShirt = /shirt|t-shirt|tshirt|\btee\b|apparel/.test(searchableProductText);
    const hasApparelSizing = options.some(opt => /size/i.test(opt.name || '')) && !excludedFitGuideProduct;
    const hasFitGuide = explicitFit || looksLikeShirt || hasApparelSizing;
    if (fitGuideButton) {
      const guideRoot = document.querySelector('[data-jol-fit-guide-root]');
      fitGuideButton.innerHTML = `<span class="jol-fit-guide__trigger-main">${guideRoot?.dataset.linkText || 'See how it fits'}</span><span class="jol-fit-guide__trigger-icon" aria-hidden="true">→</span>`;
      fitGuideButton.hidden = !hasFitGuide;
    }
    if (isBlank) {
      fitGuideButton.hidden = true;
        const norm = v => String(v || '').trim().toLowerCase().replace(/[^a-z0-9]/g,'');
      const sizeOrder = ['s','m','l','xl','2xl'];
      const sizeAliases = {s:['s','small'],m:['m','medium'],l:['l','large'],xl:['xl','xlarge'], '2xl':['2xl','xxl','xxlarge','2x','2xlarge']};
      let colorIndex = options.findIndex(o => /colou?r/i.test(o.name || ''));
      let sizeIndex = options.findIndex(o => /size/i.test(o.name || ''));
      const val = (v,i) => v[`option${i+1}`];
      if (sizeIndex < 0) sizeIndex = options.findIndex((o,idx) => (product.variants || []).some(v => Object.values(sizeAliases).flat().includes(norm(val(v,idx)))));
      if (colorIndex < 0) colorIndex = options.findIndex((o,idx) => (product.variants || []).some(v => ['black','white'].includes(norm(val(v,idx)))));
      const rawColors = colorIndex >= 0 ? [...new Set((product.variants || []).map(v => val(v,colorIndex)).filter(Boolean))] : ['Color'];
      const preferredColors = rawColors.filter(v => norm(v).includes('black') || norm(v).includes('white'));
      const colors = preferredColors.length ? preferredColors : rawColors;
      const sizes = [];
      for (const key of sizeOrder) {
        const found = (product.variants || []).map(v => val(v,sizeIndex)).find(v => sizeAliases[key].includes(norm(v)));
        if (found && !sizes.some(s => norm(s) === norm(found))) sizes.push(found);
      }
      if (!sizes.length && sizeIndex >= 0) sizes.push(...[...new Set((product.variants || []).map(v => val(v,sizeIndex)).filter(Boolean))]);
      const quantities = new Map();
      let activeColor = colors.find(c => norm(c).includes('black')) || colors[0];
      const matchVariant = (color,size) => (product.variants || []).find(v => (colorIndex < 0 || val(v,colorIndex) === color) && (sizeIndex < 0 || val(v,sizeIndex) === size));
      const showColorImage = (color) => {
        const key = norm(color);
        const matching = (product.variants || []).find(v => colorIndex < 0 || val(v,colorIndex) === color);
        const variantSrc = matching?.featured_image?.src;
        const fallback = key.includes('white') ? product.images?.[1] : product.images?.[0];
        const src = variantSrc || fallback || product.featured_image;
        if (src) { image.src = src; image.alt = `${product.title || ''} – ${color}`; }
      };
      optionsWrap.innerHTML = `<div class="jol-bulk-builder jol-bulk-builder--quickview" data-qv-bulk>
        <div class="jol-bulk-builder__head"><div><span class="jol-bulk-builder__eyebrow">Bulk blanks</span><strong>Build your 10+ shirt order</strong></div><span class="jol-bulk-builder__minimum">10 minimum</span></div>
        <p class="jol-bulk-builder__help">Choose Black, White, or both. Enter quantities by size from S–2XL.</p>
        <div class="jol-bulk-builder__colors" data-qv-bulk-colors></div>
        <div class="jol-bulk-builder__sizes" data-qv-bulk-sizes></div>
        <div class="jol-bulk-builder__summary"><span>Total shirts</span><strong data-qv-bulk-total>0</strong></div>
      </div>`;
      const colorsWrap = optionsWrap.querySelector('[data-qv-bulk-colors]');
      const sizesWrap = optionsWrap.querySelector('[data-qv-bulk-sizes]');
      const totalEl = optionsWrap.querySelector('[data-qv-bulk-total]');
      const total = () => [...quantities.values()].reduce((sum,q) => sum + Number(q || 0),0);
      const orderTotal = () => [...quantities.entries()].reduce((sum,[id,q]) => {
        const variant = (product.variants || []).find(v => String(v.id) === String(id));
        return sum + (Number(q || 0) * Number(variant?.price || product.price || 0));
      }, 0);
      const updateSummary = () => {
        const count = total();
        const amount = orderTotal();
        totalEl.textContent = count;
        price.textContent = count > 0 ? `${money(amount)} · ${count} shirt${count === 1 ? '' : 's'}` : `From ${money(Number(product.price || 0) * 10)} · ${money(product.price)}/shirt`;
        setQuickViewAddLabel(add, count >= 10 ? `Add ${count} shirt${count === 1 ? '' : 's'} · ${money(amount)}` : `Select ${10-count} more shirt${10-count === 1 ? '' : 's'}`);
        add.disabled = count < 10;
        status.textContent = count < 10 ? 'Choose at least 10 shirts across any sizes and colors.' : 'Minimum reached — ready to add to bag.';
      };
      const renderColors = () => {
        colorsWrap.innerHTML = '';
        colors.forEach(color => {
          const btn = document.createElement('button');
          btn.type='button'; btn.className='jol-bulk-color'+(color === activeColor ? ' is-selected':'');
          const key = norm(color);
          btn.innerHTML = `<span class="jol-bulk-color__swatch jol-bulk-color__swatch--${key.includes('black') ? 'black' : key.includes('white') ? 'white' : 'other'}"></span><span>${escapeHtml(color)}</span>`;
          btn.addEventListener('click',()=>{ activeColor=color; showColorImage(color); renderColors(); renderSizes(); updateSummary(); });
          colorsWrap.appendChild(btn);
        });
      };
      const renderSizes = () => {
        sizesWrap.innerHTML = '';
        sizes.forEach(size => {
          const variant = matchVariant(activeColor,size);
          const label = document.createElement('label');
          label.className='jol-bulk-size'+(!variant || !variant.available ? ' is-disabled':'');
          const q = variant ? Number(quantities.get(String(variant.id)) || 0) : 0;
          const unavailable = !variant || !variant.available;
          label.innerHTML = `<span class="jol-bulk-size__label">${escapeHtml(size)}</span><div class="jol-bulk-size__stepper"><button type="button" data-minus ${unavailable ? 'disabled':''} aria-label="Decrease ${escapeHtml(activeColor)} ${escapeHtml(size)}">−</button><input type="number" min="0" step="1" inputmode="numeric" value="${q}" placeholder="0" ${unavailable ? 'disabled':''} aria-label="${escapeHtml(activeColor)} ${escapeHtml(size)} quantity"><button type="button" data-plus ${unavailable ? 'disabled':''} aria-label="Increase ${escapeHtml(activeColor)} ${escapeHtml(size)}">+</button></div>`;
          const input=label.querySelector('input');
          const setQty=(next)=>{ if(!variant||!input)return; const q=Math.max(0,parseInt(next || '0',10)||0); input.value=q; quantities.set(String(variant.id),q); updateSummary(); };
          if (variant && input) {
            input.addEventListener('input',()=>setQty(input.value));
            input.addEventListener('focus',()=>input.select());
            label.querySelector('[data-minus]')?.addEventListener('click',()=>setQty(Number(input.value||0)-1));
            label.querySelector('[data-plus]')?.addEventListener('click',()=>setQty(Number(input.value||0)+1));
          }
          sizesWrap.appendChild(label);
        });
      };
      showColorImage(activeColor); renderColors(); renderSizes(); updateSummary();
      add.onclick = async () => {
        const items=[...quantities.entries()].filter(([,q])=>Number(q)>0).map(([id,q])=>({id:Number(id),quantity:Number(q),properties:{_jol_bulk_blank:'true',_jol_bulk_product:String(product.id || product.handle || '')}}));
        if (total() < 10 || !items.length) return;
        add.disabled=true; setQuickViewAddLabel(add,'Adding…'); status.textContent='';
        try {
          const response=await fetch(route('cart/add.js'),{method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({items})});
          if(!response.ok) throw new Error('Could not add this size mix.');
          await refreshCart();
          await animateQuickViewAdded(add);
          await closeQuickViewAfterAdd(overlay);
          // V188: keep the card-back return, but leave the bag drawer closed.
        } catch(err) { status.textContent=err.message || 'Could not add this size mix.'; updateSummary(); }
      };
      return;
    }

    optionsWrap.innerHTML = '';
    const optionKinds = options.map(opt => {
      const name = String(opt.name || '').toLowerCase();
      if (/fit|style|cut/.test(name)) return 'fit';
      if (/color|colour/.test(name)) return 'color';
      if (/size/.test(name)) return 'size';
      return 'other';
    });
    const optionGroups = [];
    let showFitPromo = false;
    options.forEach((opt, idx) => {
      const values = [...new Set((product.variants || []).map(v => v[`option${idx+1}`]).filter(Boolean))];
      if (values.length === 1 && values[0] === 'Default Title') { selected[idx] = values[0]; return; }
      selected[idx] = values[0];
      const group = document.createElement('div');
      const isFitOption = optionKinds[idx] === 'fit';
      const isSizeOption = optionKinds[idx] === 'size';
      const isColorOption = optionKinds[idx] === 'color';
      group.className = 'jol-quickview__option' + (isFitOption ? ' jol-quickview__option--fit' : '') + (isSizeOption ? ' jol-quickview__option--size' : '') + (isColorOption ? ' jol-quickview__option--color' : '');
      group.innerHTML = `<span class="jol-quickview__option-label">${escapeHtml(opt.name || `Option ${idx+1}`)}</span><div class="jol-quickview__option-values"></div>`;
      const valuesWrap = group.lastElementChild;
      const controls = [];
      values.forEach((value, valueIndex) => {
        const btn = document.createElement('button');
        btn.type='button';
        btn.className='jol-option-btn'+(isFitOption?' jol-option-btn--fit':'')+(valueIndex===0?' is-selected':'');
        btn.dataset.optionIndex=String(idx);
        btn.dataset.optionValue=value;
        btn.dataset.baseLabel=value;
        btn.innerHTML = isFitOption ? `<span>${escapeHtml(value)}</span>` : escapeHtml(value);
        btn.addEventListener('click', () => {
          if (btn.disabled) return;
          selected[idx]=value;
          valuesWrap.querySelectorAll('.jol-option-btn').forEach(b=>b.classList.toggle('is-selected',b===btn));
          syncVariant(idx);
        });
        valuesWrap.appendChild(btn);
        controls.push(btn);
      });
      optionGroups[idx] = { group, valuesWrap, controls };
      optionsWrap.appendChild(group);
      if (isFitOption && values.some(value => /fitted|relaxed/i.test(String(value)))) {
        showFitPromo = true;
      }
    });
    if (showFitPromo) {
      const promo = document.createElement('div');
      promo.className = 'jol-fit-promo jol-fit-promo--quickview';
      promo.dataset.jolFitPromo = '';
      promo.innerHTML = '<strong>Not sure which fit you’ll like more?</strong><span>Try the other fit for <b>$10 off</b>. Any graphic. Automatically applied once per account.</span>';
      if (fitGuideButton && !fitGuideButton.hidden) fitGuideButton.insertAdjacentElement('afterend', promo);
      else optionsWrap.appendChild(promo);
    }

    const dependencyIndices = (idx) => {
      const kind = optionKinds[idx];
      return optionKinds.map((otherKind, otherIdx) => ({otherKind, otherIdx})).filter(({otherKind,otherIdx}) => {
        if (otherIdx === idx) return false;
        if (kind === 'fit') return false;
        if (kind === 'color') return otherKind === 'fit';
        if (kind === 'size') return otherKind === 'fit' || otherKind === 'color';
        return otherIdx < idx;
      }).map(({otherIdx}) => otherIdx);
    };
    const combinationExists = (idx, value) => (product.variants || []).some(v => {
      if (v.available === false) return false;
      if (String(v[`option${idx+1}`] ?? '') !== String(value)) return false;
      return dependencyIndices(idx).every(depIdx => !selected[depIdx] || String(v[`option${depIdx+1}`] ?? '') === String(selected[depIdx]));
    });
    const renderQuickViewAvailability = (changedIndex=-1) => {
      optionGroups.forEach((entry, idx) => {
        if (!entry) return;
        entry.controls.forEach(btn => {
          const available = combinationExists(idx, btn.dataset.optionValue);
          btn.disabled = !available;
          btn.classList.toggle('is-unavailable', !available);
          btn.setAttribute('aria-disabled', available ? 'false' : 'true');
          const label = btn.dataset.baseLabel || btn.dataset.optionValue || '';
          const valueHtml = `<span class="jol-option-btn__value">${escapeHtml(label)}</span>`;
          // V195: unavailable options stay crossed out + unselectable without extra helper text.
          const labelHtml = valueHtml;
          btn.innerHTML = optionKinds[idx] === 'fit' ? `<span>${labelHtml}</span>` : labelHtml;
        });
        const active = entry.controls.find(btn => btn.classList.contains('is-selected'));
        if (active?.disabled && idx !== changedIndex) {
          const replacement = entry.controls.find(btn => !btn.disabled);
          if (replacement) {
            entry.controls.forEach(btn => btn.classList.toggle('is-selected', btn === replacement));
            selected[idx] = replacement.dataset.optionValue;
          }
        }
      });
    };

    let updateGiftBuilder = null;
    const syncVariant = (changedIndex=-1) => {
      renderQuickViewAvailability(changedIndex);
      let variant = (product.variants || []).find(v => selected.every((val,idx) => !val || v[`option${idx+1}`] === val) && v.available !== false);
      if (!variant) {
        // If a changed parent option invalidated a dependent value, choose the first real variant matching the parent selection.
        variant = (product.variants || []).find(v => v.available !== false && selected.every((val,idx) => {
          if (!val) return true;
          if (changedIndex >= 0 && idx > changedIndex) return true;
          return v[`option${idx+1}`] === val;
        }));
        if (variant) {
          options.forEach((_,idx) => {
            const value = variant[`option${idx+1}`];
            if (!value || !optionGroups[idx]) return;
            selected[idx] = value;
            optionGroups[idx].controls.forEach(btn => btn.classList.toggle('is-selected', btn.dataset.optionValue === value));
          });
          renderQuickViewAvailability(changedIndex);
        }
      }
      overlay.dataset.variantId = variant ? variant.id : '';
      const saleVariant = (variant && Number(variant.compare_at_price || 0) > Number(variant.price || 0))
        ? variant
        : ((product.variants || []).find(v => Number(v.compare_at_price || 0) > Number(v.price || 0)) || variant);
      if (saleVariant && Number(saleVariant.compare_at_price || 0) > Number(saleVariant.price || 0)) {
        price.innerHTML = `<span class="jol-sale-price jol-sale-price--quickview"><span class="jol-sale-price__compare">${money(saleVariant.compare_at_price)}</span><span class="jol-sale-price__current">${money(saleVariant.price)}</span></span>`;
      } else {
        price.textContent = variant ? money(variant.price) : money(product.price);
      }
      add.disabled = !variant || !variant.available;
      setQuickViewAddLabel(add, variant && variant.available ? 'Add to Bag' : 'Unavailable');
      status.textContent = '';
      if (variant && variant.featured_image && variant.featured_image.src) image.src = variant.featured_image.src;
      if (updateGiftBuilder) updateGiftBuilder(variant);
    };
    syncVariant();

    if (isGift) {
      fitGuideButton.hidden = true;
      const gift = document.createElement('div');
      gift.className = 'jol-bulk-builder jol-gift-builder jol-gift-builder--quickview';
      gift.innerHTML = `<div class="jol-bulk-builder__head"><div><span class="jol-bulk-builder__eyebrow">Gift orders</span><strong>Build your 15+ gift order</strong></div><span class="jol-bulk-builder__minimum">15 minimum</span></div>
        <p class="jol-bulk-builder__help">Order 15 or more. Add a logo for customization and any extra order details below.</p>
        <div class="jol-gift-builder__quantity"><span class="jol-bulk-size__label">Quantity</span><div class="jol-bulk-size__stepper jol-gift-builder__stepper"><button type="button" data-gift-minus>−</button><input type="number" min="15" step="1" inputmode="numeric" value="15" data-gift-qty><button type="button" data-gift-plus>+</button></div></div>
        <label class="jol-gift-builder__field"><span>Add logo <small>optional</small></span><input type="file" accept=".png,.jpg,.jpeg,.webp,.pdf" data-gift-logo></label>
        <label class="jol-gift-builder__field"><span>Extra details <small>optional</small></span><textarea rows="2" maxlength="500" placeholder="Logo placement, event name, colors, delivery notes, etc." data-gift-details></textarea></label>`;
      optionsWrap.appendChild(gift);
      const giftQty = gift.querySelector('[data-gift-qty]');
      const giftLogo = gift.querySelector('[data-gift-logo]');
      const giftDetails = gift.querySelector('[data-gift-details]');
      const cleanGiftQty = value => Math.max(15, Math.floor(Number(value) || 15));
      updateGiftBuilder = (variantArg) => {
        const count = cleanGiftQty(giftQty.value);
        giftQty.value = count;
        const variant = variantArg || (product.variants || []).find(v => String(v.id) === String(overlay.dataset.variantId));
        const unit = Number(variant?.price || product.price || 0);
        price.textContent = `From ${money(unit * 15)} · ${count} selected`;
        add.disabled = !variant || variant.available === false || count < 15;
        setQuickViewAddLabel(add, `Add ${count} gift${count === 1 ? '' : 's'} · ${money(unit * count)}`);
      };
      gift.querySelector('[data-gift-minus]').addEventListener('click', () => { giftQty.value = cleanGiftQty(Number(giftQty.value) - 1); updateGiftBuilder(); });
      gift.querySelector('[data-gift-plus]').addEventListener('click', () => { giftQty.value = cleanGiftQty(Number(giftQty.value) + 1); updateGiftBuilder(); });
      giftQty.addEventListener('input', () => updateGiftBuilder());
      updateGiftBuilder();
      add.onclick = async () => {
        const id = Number(overlay.dataset.variantId);
        const count = cleanGiftQty(giftQty.value);
        if (!id || count < 15) return;
        add.disabled = true; setQuickViewAddLabel(add,'Adding…'); status.textContent='';
        const formData = new FormData();
        formData.append('id', String(id));
        formData.append('quantity', String(count));
        formData.append('properties[_jol_bulk_gift]', 'true');
        formData.append('properties[_jol_bulk_product]', String(product.id || product.handle || ''));
        if (giftDetails.value.trim()) formData.append('properties[Order details]', giftDetails.value.trim());
        if (giftLogo.files?.[0]) formData.append('properties[Logo]', giftLogo.files[0]);
        try {
          const response = await fetch(route('cart/add.js'), {method:'POST',headers:{'Accept':'application/json'},body:formData});
          if (!response.ok) throw new Error('Could not add this gift order.');
          await refreshCart();
          await animateQuickViewAdded(add);
          await closeQuickViewAfterAdd(overlay);
          // V188: keep the card-back return, but leave the bag drawer closed.
        } catch(err) { status.textContent=err.message || 'Could not add this gift order.'; updateGiftBuilder(); }
      };
      return;
    }

    add.onclick = async () => {
      const id = Number(overlay.dataset.variantId);
      if (!id) return;
      add.disabled = true; setQuickViewAddLabel(add,'Adding…'); status.textContent='';
      try {
        const response = await fetch(route('cart/add.js'), {method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({items:[{id,quantity:1}]})});
        if (!response.ok) throw new Error('Could not add this selection.');
        await refreshCart();
        await animateQuickViewAdded(add);
        await closeQuickViewAfterAdd(overlay);
        // V188: keep the card-back return, but leave the bag drawer closed.
      } catch(err) {
        status.textContent = err.message || 'Could not add this selection.';
        syncVariant();
      }
    };
  };

  const settleQuickViewMedia = async (overlay) => {
    const image = overlay?.querySelector('[data-jol-qv-image]');
    if (!image?.src) return;
    image.loading = 'eager';
    try { await image.decode(); } catch (_) {}
    // Lock in the populated geometry before the entrance starts.
    overlay.querySelector('.jol-quickview')?.getBoundingClientRect();
    await nextPaint();
  };

  const preinitializeQuickView = () => {
    const overlay = ensureQuickView();
    const popup = overlay?.querySelector('.jol-quickview');
    if (!popup) return;
    // Build, measure, style and ask the compositor to see the shell before the first interaction.
    popup.style.willChange = 'translate, opacity';
    popup.getBoundingClientRect();
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (!overlay.classList.contains('is-open')) popup.style.removeProperty('will-change');
    }));
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', preinitializeQuickView, { once:true });
  else preinitializeQuickView();

  document.documentElement.addEventListener('animationend', e => {
    if (e.animationName === 'jolFullDetailsEnter') document.documentElement.classList.remove('jol-full-details-enter');
  });

  const openProduct = async (link) => {
    if (quickViewAnimating) return;
    closeAll();
    quickViewAnimating = true;
    lastFocus = link;
    const card = link.closest('[data-jol-product-card]');
    const overlay = ensureQuickView();
    overlay._jolResetQuickViewGesture?.();
    const title = overlay.querySelector('[data-jol-qv-title]');
    title.textContent='Loading…';
    overlay.querySelector('[data-jol-qv-description]').innerHTML='';
    overlay.querySelector('[data-jol-qv-options]').innerHTML='';
    overlay.querySelector('[data-jol-qv-price]').textContent='';
    overlay.querySelector('[data-jol-qv-details]').href='#';
    overlay.querySelector('[data-jol-qv-add]').disabled=true;

    const handle=link.dataset.productHandle;
    const productPromise=fetch(route(`products/${encodeURIComponent(handle)}.js`),{headers:{'Accept':'application/json'}})
      .then(r=>{if(!r.ok) throw new Error(); return r.json();});

    await animateCardOut(card);
    setLocked(true);
    try {
      const product=await productPromise;
      product._jolIsBlank = link.dataset.jolBlank === 'true';
      product._jolIsGift = link.dataset.jolGift === 'true';
      renderProduct(product,overlay);
      await settleQuickViewMedia(overlay);
    } catch(err) {
      quickViewHistoryActive = false;
      restoreActiveCardImmediate();
      setLocked(false);
      quickViewAnimating = false;
      window.location.replace(link.href);
      return;
    }
    if (!quickViewHistoryActive) {
      history.pushState({ ...(history.state || {}), jolQuickView: true }, '', window.location.href);
      quickViewHistoryActive = true;
    }
    await animatePopupIn(overlay);
    quickViewAnimating = false;
    overlay.querySelector('[data-jol-overlay-close]')?.focus({preventScroll:true});
  };

  // V221: transient mobile drag-hover, matching the original touch browsing behavior.
  // Touch/dragging over a card previews the desktop hover visual only while the finger is on it.
  // Moving onto another card transfers the hover immediately; lifting the finger clears it.
  // A normal tap is NOT intercepted here, so the existing click handler still opens quick view once.
  const jolMobileCardHoverMode = () => window.matchMedia('(hover: none), (pointer: coarse), (max-width: 800px)').matches;
  let jolTouchHoverCard = null;

  const setJolTouchHoverCard = card => {
    if (jolTouchHoverCard === card) return;
    if (jolTouchHoverCard) jolTouchHoverCard.classList.remove('jol-card--touch-hover');
    jolTouchHoverCard = card || null;
    if (jolTouchHoverCard) jolTouchHoverCard.classList.add('jol-card--touch-hover');
  };

  const cardAtTouchPoint = touch => {
    const hit = document.elementFromPoint(touch.clientX, touch.clientY);
    const card = hit?.closest?.('[data-jol-product-card]') || null;
    // V224: mobile homepage cards are swipe/scroll targets, not hover-preview targets.
    // Keep V221 touch-hover everywhere else (including Catalog mobile).
    if (card?.closest?.('[data-jol-deck-browser]') && window.matchMedia('(max-width:760px)').matches) return null;
    return card;
  };

  document.addEventListener('touchstart', event => {
    if (!jolMobileCardHoverMode() || event.touches.length !== 1) return;
    setJolTouchHoverCard(cardAtTouchPoint(event.touches[0]));
  }, { passive:true, capture:true });

  document.addEventListener('touchmove', event => {
    if (!jolMobileCardHoverMode() || event.touches.length !== 1) return;
    setJolTouchHoverCard(cardAtTouchPoint(event.touches[0]));
  }, { passive:true, capture:true });

  const clearJolTouchHover = () => setJolTouchHoverCard(null);
  document.addEventListener('touchend', clearJolTouchHover, { passive:true, capture:true });
  document.addEventListener('touchcancel', clearJolTouchHover, { passive:true, capture:true });

  document.addEventListener('click', async e => {
    const cardAdd=e.target.closest('[data-jol-card-add]');
    if (cardAdd) {
      e.preventDefault();
      e.stopPropagation();
      const id=Number(cardAdd.dataset.variantId);
      if (!id || cardAdd.disabled) return;
      const label=cardAdd.querySelector('span');
      const original=label?.textContent || 'Add';
      cardAdd.disabled=true;
      if (label) label.textContent='Adding…';
      try {
        const response=await fetch(route('cart/add.js'), {method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({items:[{id,quantity:1}]})});
        if (!response.ok) throw new Error();
        await openCart(cardAdd);
      } catch(err) {
        cardAdd.disabled=false;
        if (label) label.textContent=original;
      }
      return;
    }
    const productLink=e.target.closest('[data-jol-product-open]');
    if (productLink && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button !== 1) {
      e.preventDefault();

      {
        const alertHandle = productLink.dataset.productHandle || '';
        const alertEligible = productLink.dataset.jolBlank !== 'true' && productLink.dataset.jolGift !== 'true' && alertHandle !== 'weekly-giveaway';
        document.dispatchEvent(new CustomEvent('jol:product-view', { detail:{ handle:alertHandle, eligible:alertEligible } }));
      }
      openProduct(productLink);
      return;
    }
    const cartTrigger=e.target.closest('[data-jol-cart-trigger]');
    if (cartTrigger) { e.preventDefault(); openCart(cartTrigger); }
  });
  window.addEventListener('popstate', () => {
    if (window.__jolSuppressQuickViewPopstate) {
      window.__jolSuppressQuickViewPopstate = false;
      return;
    }
    const fitGuideOverlay = document.querySelector('[data-jol-fit-guide-overlay]');
    if (fitGuideOverlay && !fitGuideOverlay.hidden) return;
    const overlay = document.querySelector('[data-jol-quickview-overlay].is-open');
    if (!overlay) return;
    quickViewHistoryActive = false;
    closeQuickView('right', {skipHistory:true});
  });
  document.addEventListener('keydown', e => {
    if (e.key !== 'Escape') return;
    const zoomViewer = document.querySelector('[data-jol-qv-zoom-overlay].is-open');
    if (zoomViewer) { e.preventDefault(); zoomViewer._jolCloseZoom?.(); return; }
    const quickView = document.querySelector('[data-jol-quickview-overlay].is-open');
    if (quickView) closeQuickView('down');
    else closeAll();
  });
})();


// V143 — storefront search UI removed.

// v91 — enforce 10-shirt minimum for blank products after they reach the cart.
(() => {
  if (!document.body?.classList.contains('template-cart')) return;
  const rows = () => [...document.querySelectorAll('tr[data-jol-blank-product]')];
  const rowQty = row => Number(row.querySelector('input[data-cart-line]')?.value || 0);
  const productTotal = key => rows().filter(row => row.dataset.jolBlankProduct === key).reduce((sum,row) => sum + rowQty(row), 0);
  const warn = (row, message='Blank styles require at least 10 shirts per product.') => {
    let note = row.querySelector('.jol-cart-blank-warning');
    if (!note) {
      note = document.createElement('div');
      note.className = 'jol-cart-blank-warning';
      row.querySelector('.cart-items__quantity-controls')?.insertAdjacentElement('afterend', note);
    }
    note.textContent = message;
    setTimeout(() => { if (note) note.textContent = ''; }, 3200);
  };
  const validateCheckout = () => {
    const keys = [...new Set(rows().map(row => row.dataset.jolBlankProduct))];
    const invalid = keys.some(key => productTotal(key) > 0 && productTotal(key) < 10);
    document.querySelectorAll('#checkout, [name="checkout"]').forEach(btn => {
      if (invalid) { btn.disabled = true; btn.dataset.jolBlankDisabled = 'true'; }
      else if (btn.dataset.jolBlankDisabled === 'true') { btn.disabled = false; delete btn.dataset.jolBlankDisabled; }
    });
    document.querySelectorAll('.additional-checkout-buttons').forEach(el => el.style.display = invalid ? 'none' : '');
  };
  document.addEventListener('click', e => {
    const row = e.target.closest('tr[data-jol-blank-product]');
    if (!row) return;
    const key = row.dataset.jolBlankProduct;
    const current = productTotal(key);
    if (e.target.closest('.quantity-minus') && current <= 10) {
      e.preventDefault(); e.stopImmediatePropagation(); warn(row); return;
    }
    if (e.target.closest('.cart-items__remove') && current - rowQty(row) < 10 && current - rowQty(row) > 0) {
      e.preventDefault(); e.stopImmediatePropagation(); warn(row, 'Keep at least 10 shirts of this blank style, or remove the entire blank product.');
    }
  }, true);
  const validateBlankInput = e => {
    const input = e.target.closest?.('tr[data-jol-blank-product] input[data-cart-line]');
    if (!input) return;
    const row = input.closest('tr[data-jol-blank-product]');
    const key = row.dataset.jolBlankProduct;
    const others = rows().filter(r => r !== row && r.dataset.jolBlankProduct === key).reduce((sum,r) => sum + rowQty(r),0);
    const next = Number(input.value || 0);
    if (next + others > 0 && next + others < 10) {
      input.value = Math.max(0, 10 - others);
      warn(row);
      e.preventDefault(); e.stopImmediatePropagation();
    }
    validateCheckout();
  };
  document.addEventListener('change', validateBlankInput, true);
  document.addEventListener('blur', validateBlankInput, true);
  const run = () => validateCheckout();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run, {once:true}); else run();
  new MutationObserver(validateCheckout).observe(document.documentElement, {childList:true, subtree:true});
})();

// v26 removed in v94: Fit & Feel remains below product; no separate Fit notes card in product details or popup.

// v27 — Enforce real Shopify variant combinations in PDP + quick-add pickers.
// A color/fit value may exist on the product while a particular combination does not.
// This enhancer uses the full variant matrix so impossible combinations cannot be selected or added.
(() => {
  const parseMatrix = picker => {
    const script = picker.querySelector('script[data-jol-variant-matrix]');
    if (!script) return [];
    try {
      const variants = JSON.parse(script.textContent || '[]');
      return Array.isArray(variants) ? variants : [];
    } catch (_) {
      return [];
    }
  };

  const groups = picker => [...picker.querySelectorAll('.variant-option')].map(host => {
    const radios = [...host.querySelectorAll('input[type="radio"][data-option-name]')];
    const select = host.querySelector('select');
    const first = radios[0] || select?.options?.[0];
    if (!first) return null;
    const name = first.dataset.optionName || select?.name?.replace(/^options\[|\]$/g, '') || '';
    return { host, name, radios, select };
  }).filter(Boolean);

  const selectedValues = picker => groups(picker).map(group => {
    if (group.radios.length) return group.radios.find(input => input.checked)?.value || '';
    return group.select?.value || '';
  });

  const variantOptions = variant => {
    if (Array.isArray(variant.options)) return variant.options.map(String);
    return [variant.option1, variant.option2, variant.option3].filter(v => v != null).map(String);
  };

  const exactVariant = (variants, values) => variants.find(variant => {
    const opts = variantOptions(variant);
    return values.length === opts.length && values.every((value, index) => String(value) === String(opts[index]));
  });

  const optionKind = name => {
    const key = String(name || '').toLowerCase();
    if (/fit|style|cut/.test(key)) return 'fit';
    if (/colou?r/.test(key)) return 'color';
    if (/size/.test(key)) return 'size';
    return 'other';
  };

  const validForValue = (variants, current, optionGroups, groupIndex, candidate) => {
    const kind = optionKind(optionGroups[groupIndex]?.name);
    const dependencyIndices = optionGroups.map((group, index) => ({ index, kind: optionKind(group.name) })).filter(item => {
      if (item.index === groupIndex) return false;
      if (kind === 'fit') return false;
      if (kind === 'color') return item.kind === 'fit';
      if (kind === 'size') return item.kind === 'fit' || item.kind === 'color';
      return item.index < groupIndex;
    }).map(item => item.index);

    return variants.some(variant => {
      if (variant.available === false) return false;
      const opts = variantOptions(variant);
      if (String(opts[groupIndex] ?? '') !== String(candidate)) return false;
      return dependencyIndices.every(index => {
        const selected = current[index];
        return !selected || String(opts[index] ?? '') === String(selected);
      });
    });
  };

  const setControlAvailability = (control, enabled) => {
    if (control instanceof HTMLInputElement) {
      const isProductPageControl = Boolean(control.closest('.template-product'));
      // V188: on PDPs an unavailable combination remains selectable so the shopper
      // can inspect it; the purchase button communicates Unavailable instead.
      control.disabled = isProductPageControl ? false : !enabled;
      control.setAttribute('aria-disabled', enabled ? 'false' : 'true');
      control.dataset.jolCombinationAvailable = enabled ? 'true' : 'false';
      control.closest('label')?.classList.toggle('jol-variant-impossible', !enabled);
    } else if (control instanceof HTMLOptionElement) {
      const isProductPageControl = Boolean(control.closest('.template-product'));
      control.disabled = isProductPageControl ? false : !enabled;
      control.dataset.jolCombinationAvailable = enabled ? 'true' : 'false';
    }
  };

  const findFirstValid = (variants, current, optionGroups, groupIndex, controls) => controls.find(control => {
    const value = control.value;
    return validForValue(variants, current, optionGroups, groupIndex, value);
  });

  const syncAddToCart = (picker, variant) => {
    const scope = picker.closest('product-component') || picker.closest('quick-add-dialog') || picker.parentElement;
    if (!scope) return;
    const valid = Boolean(variant && variant.available !== false);

    scope.querySelectorAll('product-form-component input[name="id"], form[action*="/cart/add"] input[name="id"]').forEach(input => {
      if (!(input instanceof HTMLInputElement)) return;
      if (valid) {
        input.value = String(variant.id);
        input.disabled = false;
      } else {
        input.disabled = true;
      }
    });

    scope.querySelectorAll('button[name="add"], .add-to-cart-button').forEach(button => {
      if (!(button instanceof HTMLButtonElement)) return;
      // Do not disable a "Choose options" launcher outside the modal.
      if (button.classList.contains('quick-add__button--choose')) return;
      button.disabled = !valid;
      button.setAttribute('aria-disabled', valid ? 'false' : 'true');
    });
  };

  const reconcile = (picker, changedIndex = -1) => {
    const variants = parseMatrix(picker);
    const optionGroups = groups(picker);
    if (!variants.length || !optionGroups.length) return;

    let current = selectedValues(picker);

    const processingOrder = optionGroups.map((group, index) => ({
      index,
      rank: optionKind(group.name) === 'fit' ? 0 : optionKind(group.name) === 'color' ? 1 : optionKind(group.name) === 'size' ? 2 : 3 + index
    })).sort((a, b) => a.rank - b.rank);

    processingOrder.forEach(({ index: groupIndex }) => {
      const group = optionGroups[groupIndex];
      const controls = group.radios.length ? group.radios : [...(group.select?.options || [])];
      controls.forEach(control => setControlAvailability(control, validForValue(variants, current, optionGroups, groupIndex, control.value)));

      const selectedControl = group.radios.length
        ? group.radios.find(input => input.checked)
        : group.select?.selectedOptions?.[0];

      if (selectedControl && selectedControl.disabled && groupIndex !== changedIndex) {
        const replacement = findFirstValid(variants, current, optionGroups, groupIndex, controls);
        if (replacement) {
          if (replacement instanceof HTMLInputElement) {
            replacement.checked = true;
          } else if (group.select) {
            group.select.value = replacement.value;
          }
          current[groupIndex] = replacement.value;
        }
      }
    });

    const variant = exactVariant(variants, current);
    syncAddToCart(picker, variant && variant.available !== false ? variant : null);
  };

  const enhance = (picker) => {
    if (!(picker instanceof HTMLElement) || picker.dataset.jolVariantMatrixEnhanced === 'true') return;
    if (!parseMatrix(picker).length) return;
    picker.dataset.jolVariantMatrixEnhanced = 'true';

    picker.addEventListener('change', event => {
      const optionGroups = groups(picker);
      const target = event.target;
      let changedIndex = -1;
      optionGroups.forEach((group, index) => {
        if (group.host.contains(target)) changedIndex = index;
      });
      // Capture runs before the theme's native variant handler. This lets us correct dependent
      // Color/Size choices first, so Shopify receives only a real variant combination.
      reconcile(picker, changedIndex);
    }, true);

    reconcile(picker);
  };

  const scan = (scope = document) => scope.querySelectorAll('variant-picker').forEach(enhance);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  else scan();

  new MutationObserver(mutations => {
    if (mutations.some(m => [...m.addedNodes].some(node => node.nodeType === 1 && (node.matches?.('variant-picker') || node.querySelector?.('variant-picker'))))) {
      scan();
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();

// V90 — enforce 15-piece minimum for Luck Jar Gifts on the full cart page.
(() => {
  if (!document.body?.classList.contains('template-cart')) return;
  const rows = () => [...document.querySelectorAll('tr[data-jol-gift-product]')];
  const rowQty = row => Number(row.querySelector('input[data-cart-line]')?.value || 0);
  const productTotal = key => rows().filter(row => row.dataset.jolGiftProduct === key).reduce((sum,row) => sum + rowQty(row), 0);
  const warn = (row, message='Gift styles require at least 15 pieces per product.') => {
    let note = row.querySelector('.jol-cart-gift-warning');
    if (!note) {
      note = document.createElement('div');
      note.className = 'jol-cart-blank-warning jol-cart-gift-warning';
      row.querySelector('.cart-items__quantity-controls')?.insertAdjacentElement('afterend', note);
    }
    note.textContent = message;
    setTimeout(() => { if (note) note.textContent = ''; }, 3200);
  };
  const validateCheckout = () => {
    const keys = [...new Set(rows().map(row => row.dataset.jolGiftProduct))];
    const invalid = keys.some(key => productTotal(key) > 0 && productTotal(key) < 15);
    document.querySelectorAll('#checkout, [name="checkout"]').forEach(btn => {
      if (invalid) { btn.disabled = true; btn.dataset.jolGiftDisabled = 'true'; }
      else if (btn.dataset.jolGiftDisabled === 'true') { btn.disabled = false; delete btn.dataset.jolGiftDisabled; }
    });
    if (invalid) document.querySelectorAll('.additional-checkout-buttons').forEach(el => el.style.display = 'none');
  };
  document.addEventListener('click', e => {
    const row = e.target.closest('tr[data-jol-gift-product]');
    if (!row) return;
    const key = row.dataset.jolGiftProduct;
    const current = productTotal(key);
    if (e.target.closest('.quantity-minus') && current <= 15) {
      e.preventDefault(); e.stopImmediatePropagation(); warn(row); return;
    }
    if (e.target.closest('.cart-items__remove') && current - rowQty(row) < 15 && current - rowQty(row) > 0) {
      e.preventDefault(); e.stopImmediatePropagation(); warn(row, 'Keep at least 15 pieces of this gift style, or remove the entire gift product.');
    }
  }, true);
  const validateGiftInput = e => {
    const input = e.target.closest?.('tr[data-jol-gift-product] input[data-cart-line]');
    if (!input) return;
    const row = input.closest('tr[data-jol-gift-product]');
    const key = row.dataset.jolGiftProduct;
    const others = rows().filter(r => r !== row && r.dataset.jolGiftProduct === key).reduce((sum,r) => sum + rowQty(r),0);
    const next = Number(input.value || 0);
    if (next + others > 0 && next + others < 15) {
      input.value = Math.max(0, 15 - others);
      warn(row);
      e.preventDefault(); e.stopImmediatePropagation();
    }
    validateCheckout();
  };
  document.addEventListener('change', validateGiftInput, true);
  document.addEventListener('blur', validateGiftInput, true);
  const run = () => validateCheckout();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run, {once:true}); else run();
  new MutationObserver(validateCheckout).observe(document.documentElement, {childList:true, subtree:true});
})();


// V146 — keep product-page titles fully visible within a hard maximum of two lines.
(() => {
  if (!document.body?.classList.contains('template-product')) return;
  let titleObserver;
  const fitProductTitle = () => {
    const title = document.querySelector('.template-product .product-details h1');
    if (!title) return;
    title.style.removeProperty('font-size');
    title.style.removeProperty('white-space');
    const initial = parseFloat(getComputedStyle(title).fontSize) || 54;
    let size = initial;
    const minSize = 10;
    const forceOneLine = isMobileMotion() && /relaxed\s+blanks?.*10\s*\+/i.test(title.textContent || '');
    if (forceOneLine) {
      title.style.setProperty('white-space','nowrap','important');
      while (size > 14 && title.scrollWidth > title.clientWidth + 1) {
        size -= .5;
        title.style.fontSize = `${size}px`;
      }
      return;
    }
    const fitsTwoLines = () => {
      const c = getComputedStyle(title);
      const lineHeight = parseFloat(c.lineHeight) || (parseFloat(c.fontSize) || size) * .98;
      return title.scrollHeight <= lineHeight * 2 + 2;
    };
    while (size > minSize && !fitsTwoLines()) {
      size -= .5;
      title.style.fontSize = `${size}px`;
    }
  };
  const scheduleFit = () => requestAnimationFrame(() => requestAnimationFrame(fitProductTitle));
  const start = () => {
    scheduleFit();
    const title = document.querySelector('.template-product .product-details h1');
    if (title && 'ResizeObserver' in window) {
      titleObserver?.disconnect();
      titleObserver = new ResizeObserver(scheduleFit);
      titleObserver.observe(title.parentElement || title);
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once:true});
  else start();
  window.addEventListener('resize', scheduleFit, {passive:true});
  document.fonts?.ready?.then(scheduleFit).catch?.(() => {});
})();

// V144 — product payment helper text follows the product description brown.
(() => {
  if (!document.body?.classList.contains('template-product')) return;
  const brown = '#594b3d';
  const applyPaymentColor = () => {
    if (!document.body.classList.contains('template-product')) return;
    document.querySelectorAll('shopify-payment-terms').forEach(host => {
      host.style.setProperty('color', brown, 'important');
      const paintShadow = () => {
        const root = host.shadowRoot;
        if (!root) return;
        root.querySelectorAll('#shopify-installments-cta, a, button, span, p').forEach(el => {
          el.style.setProperty('color', brown, 'important');
        });
      };
      paintShadow();
      customElements.whenDefined('shopify-payment-terms').then(paintShadow).catch(() => {});
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', applyPaymentColor, {once:true});
  else applyPaymentColor();
  new MutationObserver(applyPaymentColor).observe(document.documentElement, {childList:true, subtree:true});
})();


// V153 — swiping anywhere on the splash triggers the same transition as tapping the jar.
document.addEventListener('DOMContentLoaded', () => {
  const gate = document.querySelector('[data-jol-splash]');
  const enter = gate?.querySelector('[data-jol-enter]');
  if (!gate || !enter) return;

  let startX = 0;
  let startY = 0;
  let pointerId = null;

  gate.addEventListener('pointerdown', (event) => {
    if (event.button != null && event.button !== 0) return;
    pointerId = event.pointerId;
    startX = event.clientX;
    startY = event.clientY;
  });

  gate.addEventListener('pointerup', (event) => {
    if (pointerId !== event.pointerId) return;
    const distance = Math.hypot(event.clientX - startX, event.clientY - startY);
    pointerId = null;
    if (distance < 45 || gate.classList.contains('is-entering')) return;
    event.preventDefault();
    enter.click();
  });

  gate.addEventListener('pointercancel', () => { pointerId = null; });
});

/* ---------- V162 — combined Shipping & Return policy title ---------- */
(() => {
  if (window.location.pathname !== '/policies/refund-policy') return;
  const setLuckJarPolicyHeading = () => {
    if (window.location.pathname !== '/policies/refund-policy') return;
    const title = document.querySelector('.shopify-policy__title');
    if (!title) return;
    let heading = title.querySelector('h1');
    if (!heading) {
      heading = document.createElement('h1');
      title.replaceChildren(heading);
    }
    heading.textContent = 'Shipping & Return Policy';
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setLuckJarPolicyHeading, { once: true });
  else setLuckJarPolicyHeading();
  document.addEventListener('shopify:section:load', setLuckJarPolicyHeading);
})();


// V230 — keep browser chrome synchronized after iOS/WebKit BFCache restores.
window.addEventListener('pageshow', () => {
  const splash = document.querySelector('[data-jol-splash]');
  if (!splash || !window.jolSetBrowserChrome) return;
  let entered = document.documentElement.classList.contains('jol-entered-session') || document.body.classList.contains('jol-entered');
  try { entered = entered || window.sessionStorage.getItem('jolSplashEntered') === '1'; } catch (e) {}
  const color = entered
    ? (document.querySelector('[data-jol-homepage]')?.dataset.jolBrowserChromeColor || '#1f120a')
    : (splash.dataset.jolBrowserChromeColor || '#214b32');
  window.jolSetBrowserChrome(entered ? 'store' : 'splash', color);
});
